// AUTO-GENERATED FILE - DO NOT EDIT BY HAND
// Generated from docs/3-system/00-concept-registers/*.yaml by scripts/buildConceptRegisters.ts
// The concept registers are the product's conceptual vocabulary (schema concept-register/v2).
// Status contract: registers with status "active" must match production behavior.

export type ConceptRegisterStatus = 'active' | 'draft' | 'proposed' | 'deprecated' | 'under_review';

export interface ConceptRegisterValue {
  slug: string;
  description?: string;
  [attribute: string]: unknown;
}

export interface ConceptRegisterDimension {
  slug: string;
  description?: string;
  type?: string;
  values?: ConceptRegisterValue[];
  spec?: Record<string, unknown>;
}

export interface ConceptRegister {
  family: string;
  name: string;
  slug: string;
  type: string;
  status: ConceptRegisterStatus;
  tier?: number;
  domain?: string;
  description?: string;
  purpose?: string;
  dimensions?: ConceptRegisterDimension[];
  values?: ConceptRegisterValue[];
  value_groups?: Record<string, unknown>;
  transitions?: unknown;
  parent?: string | null;
  children?: string[];
  tags?: string[];
  synonyms?: string[];
}

export const CONCEPT_REGISTERS: ConceptRegister[] = [
  {
    "family": "Governance & Authority",
    "name": "Decision Rights",
    "slug": "decision-rights",
    "type": "matrix",
    "status": "draft",
    "tier": 1,
    "domain": "Governance, Authority",
    "description": "Explicit assignment of authority over a specific class of decision within a defined organizational context. Specifies who can initiate, decide, veto, and escalate — and under what conditions.\n",
    "purpose": "Make authority visible, auditable, and queryable. Prevent implicit or assumed authority from creating governance failures. Enable \"who decides this?\" to have a clear, stored answer.\n",
    "dimensions": [
      {
        "slug": "decision_class",
        "description": "what category of decision (e.g., Q2 Prioritization, Hiring)"
      },
      {
        "slug": "org_context",
        "description": "which org unit or scope this right applies to"
      },
      {
        "slug": "initiator_role",
        "description": "who can start this decision process"
      },
      {
        "slug": "decider_role",
        "description": "who makes the final call"
      },
      {
        "slug": "veto_role",
        "description": "who can block the decision"
      },
      {
        "slug": "escalation_path",
        "description": "where disputes go"
      },
      {
        "slug": "execution_mode",
        "description": "centralized | multi-level | delegated"
      },
      {
        "slug": "applicable_levels",
        "description": "which org tiers are involved"
      }
    ],
    "values": [
      {
        "slug": "fixed",
        "description": "authority is permanently assigned"
      },
      {
        "slug": "delegated",
        "description": "authority is transferred with constraints"
      },
      {
        "slug": "conditional",
        "description": "authority depends on satisfying a condition"
      }
    ],
    "parent": null,
    "children": [
      "delegation",
      "permissions-authorization",
      "catchball-governance"
    ],
    "tags": [
      "governance",
      "authority",
      "accountability",
      "audit"
    ],
    "synonyms": [
      "authority assignment",
      "decision authority",
      "decision ownership"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Delegation",
    "slug": "delegation",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Governance, Authority Transfer",
    "description": "A pattern within Decision Rights where authority over a specific decision is transferred from one party to another, with explicit constraints on depth, recipients, duration, and reversibility.\n",
    "purpose": "Enable authority to flow to where the decision is best made, while preserving accountability and preventing runaway delegation chains.\n",
    "dimensions": [
      {
        "slug": "delegator_role",
        "description": "who is transferring authority"
      },
      {
        "slug": "recipient_role",
        "description": "who is receiving authority"
      },
      {
        "slug": "decision_class",
        "description": "what is being delegated"
      },
      {
        "slug": "depth_limit",
        "description": "how many further delegation steps are allowed (0 = none)"
      },
      {
        "slug": "duration",
        "description": "when does delegation expire"
      },
      {
        "slug": "conditions",
        "description": "any conditions on the delegation (e.g., notify delegator)"
      },
      {
        "slug": "reversibility",
        "description": "can delegator revoke? under what conditions?"
      }
    ],
    "values": [
      {
        "slug": "active",
        "description": "delegation is in effect"
      },
      {
        "slug": "expired",
        "description": "delegation period ended"
      },
      {
        "slug": "revoked",
        "description": "delegator withdrew"
      },
      {
        "slug": "pending_acceptance",
        "description": "recipient has not yet accepted"
      }
    ],
    "parent": "decision-rights",
    "children": [],
    "tags": [
      "governance",
      "authority-transfer",
      "accountability"
    ],
    "synonyms": [
      "authority delegation",
      "authority transfer",
      "mandate"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Permissions / Authorization",
    "slug": "permissions-authorization",
    "type": "hierarchy",
    "status": "draft",
    "tier": 1,
    "domain": "Access Control, Governance Mechanism",
    "description": "A three-layer model governing what actions each role can take, in what context, and under what conditions. Deny-by-default: all actions are forbidden unless explicitly granted.\n",
    "purpose": "Enforce explicit access control at runtime. Prevent implicit permission inheritance. Make \"can this person do this action?\" answerable with a deterministic, auditable yes/no.\n",
    "dimensions": [
      {
        "slug": "layer",
        "description": "capability | scope | policy (see values)",
        "values": [
          {
            "slug": "capability",
            "description": "what can this role do?"
          },
          {
            "slug": "scope",
            "description": "in what context?"
          },
          {
            "slug": "policy",
            "description": "under what conditions?"
          }
        ]
      },
      {
        "slug": "role",
        "description": "which role this grant applies to"
      },
      {
        "slug": "capability",
        "description": "what action is allowed (e.g., can_toss_ball)"
      },
      {
        "slug": "scope",
        "description": "what context the capability applies in"
      },
      {
        "slug": "policy",
        "description": "any conditions that further restrict the grant"
      },
      {
        "slug": "cascade_mode",
        "description": "all | children | none (how roles inherit to sub-nodes)",
        "values": [
          {
            "slug": "all",
            "description": "permission cascades to all child org nodes"
          },
          {
            "slug": "children",
            "description": "permission cascades one level down only"
          },
          {
            "slug": "none",
            "description": "permission is explicitly scoped; no cascade"
          }
        ]
      },
      {
        "slug": "default",
        "description": "deny (always deny-by-default)"
      }
    ],
    "parent": "decision-rights",
    "children": [],
    "tags": [
      "access-control",
      "security",
      "governance",
      "deny-by-default",
      "audit"
    ],
    "synonyms": [
      "access control",
      "authorization model",
      "role permissions"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Organizational Hierarchy",
    "slug": "organizational-hierarchy",
    "type": "hierarchy",
    "status": "draft",
    "tier": 1,
    "domain": "Organization Structure, Authority Context",
    "description": "The tree structure of an organization's units, roles, and people. Provides the structural context in which permissions are scoped, Catchball tiers are derived, and governance rules are applied.\n",
    "purpose": "Give the system an explicit model of the organization so that multi-level governance, role scoping, and tier-based features can be automated from structure rather than hard-coded.\n",
    "dimensions": [
      {
        "slug": "org_unit_id",
        "description": "unique identifier for the unit"
      },
      {
        "slug": "parent_id",
        "description": "parent unit (null for root)"
      },
      {
        "slug": "path",
        "description": "materialized path from root (e.g., /company/eng/product)"
      },
      {
        "slug": "depth",
        "description": "integer depth from root (0=root/executive)"
      },
      {
        "slug": "unit_type",
        "description": "division | department | team | squad | individual",
        "values": [
          {
            "slug": "division"
          },
          {
            "slug": "department"
          },
          {
            "slug": "team"
          },
          {
            "slug": "squad"
          },
          {
            "slug": "individual"
          }
        ]
      },
      {
        "slug": "assigned_roles",
        "description": "roles assigned at this node"
      },
      {
        "slug": "inheritable",
        "description": "whether assignments propagate to children (default: false)"
      }
    ],
    "parent": null,
    "children": [
      "tier-derivation",
      "org-hierarchy-admin"
    ],
    "tags": [
      "organization",
      "structure",
      "hierarchy",
      "governance-context",
      "tier-derivation"
    ],
    "synonyms": [
      "org tree",
      "organizational structure",
      "org chart"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Tier Derivation",
    "slug": "tier-derivation",
    "type": "flat_list",
    "status": "draft",
    "tier": 2,
    "domain": "Organization Structure, Catchball Context",
    "description": "The automatic assignment of a governance tier (executive, middle, bottom) to an org unit based on its relative depth in the Organizational Hierarchy. Tier determines ball routing and judgment-lane responsibilities in Catchball.\n",
    "purpose": "Allow Catchball Governance to use org structure to determine \"who is executive vs. middle vs. bottom\" without hard-coding it per workspace.\n",
    "dimensions": [
      {
        "slug": "org_depth",
        "description": "integer depth from root"
      },
      {
        "slug": "derived_tier",
        "description": "executive | middle | bottom"
      },
      {
        "slug": "derivation_rule",
        "description": "which rule maps depth to tier"
      }
    ],
    "values": [
      {
        "slug": "executive",
        "description": "root / near-root levels; can toss, accept, ratify"
      },
      {
        "slug": "middle",
        "description": "intermediate levels; work lane; involve, propose"
      },
      {
        "slug": "bottom",
        "description": "leaf levels; IC work; contribute, estimate"
      }
    ],
    "parent": "organizational-hierarchy",
    "children": [],
    "tags": [
      "tier",
      "catchball",
      "org-structure",
      "derived-property"
    ],
    "synonyms": [
      "org tier",
      "governance tier",
      "authority tier"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Catchball Governance",
    "slug": "catchball-governance",
    "type": "hybrid",
    "status": "draft",
    "tier": 1,
    "domain": "Multi-Level Governance, Decision Choreography",
    "description": "A structured, multi-level decision choreography in which a request travels down through organizational tiers (as a Request Ball), receives judgment work and proposals at each level, and returns upward as a Proposal Ball for ratification. Each level contributes explicitly before passing the ball.\n",
    "purpose": "Create transparent, ratified decisions with bidirectional feedback across org levels. Prevent top-down command-and-control without feedback, and bottom-up noise without ratification. Make multi-level decision flow auditable and deterministic.\n",
    "dimensions": [
      {
        "slug": "loop_id",
        "description": "unique identifier for the Catchball loop"
      },
      {
        "slug": "decision_class",
        "description": "what decision is being made"
      },
      {
        "slug": "initiating_tier",
        "description": "which tier tossed the ball first"
      },
      {
        "slug": "current_possession",
        "description": "which tier/unit currently has the ball"
      },
      {
        "slug": "ball_stance",
        "description": "request | proposal | transit-down | transit-up | complete",
        "values": [
          {
            "slug": "request",
            "description": "ball going down; seeking input"
          },
          {
            "slug": "proposal",
            "description": "ball going up; offering answer"
          },
          {
            "slug": "transit-down",
            "description": "ball in motion downward"
          },
          {
            "slug": "transit-up",
            "description": "ball in motion upward"
          },
          {
            "slug": "complete",
            "description": "loop resolved"
          }
        ]
      },
      {
        "slug": "loop_state",
        "description": "OPEN | PROPOSED | REVISION_REQUESTED | ACCEPTED | FINAL_REJECTED",
        "values": [
          {
            "slug": "OPEN",
            "description": "ball tossed; receiver must act"
          },
          {
            "slug": "PROPOSED",
            "description": "proposal returned; tossing tier reviewing"
          },
          {
            "slug": "REVISION_REQUESTED",
            "description": "revision required; ball returns with feedback"
          },
          {
            "slug": "ACCEPTED",
            "description": "proposal accepted; loop ends"
          },
          {
            "slug": "FINAL_REJECTED",
            "description": "proposal rejected; loop ends"
          }
        ]
      },
      {
        "slug": "loop_status",
        "description": "in_flight | blocked",
        "values": [
          {
            "slug": "in_flight",
            "description": "active; someone has possession and is working"
          },
          {
            "slug": "blocked",
            "description": "cannot advance; blocker is named"
          }
        ]
      },
      {
        "slug": "loop_intent",
        "description": "structured payload (see Loop Intent Register)"
      },
      {
        "slug": "readiness_gates",
        "description": "checklist of gates before ball can advance"
      },
      {
        "slug": "blockers",
        "description": "named reasons preventing advancement"
      },
      {
        "slug": "iteration_count",
        "description": "how many revision rounds have occurred"
      },
      {
        "slug": "max_iterations",
        "description": "maximum allowed revision rounds"
      }
    ],
    "parent": "decision-rights",
    "children": [
      "ball-stance",
      "legitimacy-marking",
      "catchball-loop-state",
      "catchball-loop-status",
      "ball-waiting-state",
      "loop-intent",
      "loop-readiness-gates",
      "catchball-blocker",
      "loop-clarification-request"
    ],
    "tags": [
      "governance",
      "decision-flow",
      "multi-level",
      "ratification",
      "authority-exercise",
      "catchball"
    ],
    "synonyms": [
      "catchball",
      "multi-level decision flow",
      "governance choreography",
      "hoshin catchball"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Ball Stance",
    "slug": "ball-stance",
    "type": "flat_list",
    "status": "draft",
    "tier": 2,
    "domain": "Catchball Governance",
    "description": "The directional and semantic state of the ball in a Catchball loop. Communicates whether the ball is a request going down, a proposal going up, in transit, or complete. Also encodes the legitimacy level of the payload.\n",
    "purpose": "Make the direction and intent of ball movement unambiguous at a glance. Distinct from Loop State (which tracks the lifecycle stage); Ball Stance encodes what the ball *is* right now.\n",
    "dimensions": [
      {
        "slug": "stance_type",
        "description": "see values"
      },
      {
        "slug": "direction",
        "description": "down | up | none"
      },
      {
        "slug": "payload_type",
        "description": "request_payload | proposal_payload | null"
      }
    ],
    "values": [
      {
        "slug": "request",
        "description": "going down; seeking input; carries Loop Intent"
      },
      {
        "slug": "proposal",
        "description": "going up; offering answer; carries Interpretation"
      },
      {
        "slug": "transit-down",
        "description": "in motion downward"
      },
      {
        "slug": "transit-up",
        "description": "in motion upward"
      },
      {
        "slug": "complete",
        "description": "loop resolved; no further movement"
      }
    ],
    "parent": "catchball-governance",
    "children": [],
    "tags": [
      "catchball",
      "ball-direction",
      "semantic-state"
    ],
    "synonyms": [
      "ball direction",
      "ball type"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Legitimacy Marking",
    "slug": "legitimacy-marking",
    "type": "flat_list",
    "status": "draft",
    "tier": 2,
    "domain": "Catchball Governance, Request Framing",
    "description": "A signal on a downward Request Ball that communicates the sending tier's confidence level in the request. Tells the receiving tier how firm the constraints and asks are, and how much pushback is appropriate.\n",
    "purpose": "Calibrate the receiving tier's response posture. A 'draft' marking invites open exploration; a 'preliminary' marking means constraints are serious even if not fully locked.\n",
    "dimensions": [
      {
        "slug": "marking_type",
        "description": "see values"
      },
      {
        "slug": "expected_posture",
        "description": "how receiver should interpret the openness"
      }
    ],
    "values": [
      {
        "slug": "draft",
        "description": "exploratory; thinking out loud; constraints may change; pushback welcome"
      },
      {
        "slug": "proposed",
        "description": "considered; ready for discussion; directionally firm"
      },
      {
        "slug": "preliminary",
        "description": "serious; constraints are real; changes require justification"
      }
    ],
    "parent": "catchball-governance",
    "children": [],
    "tags": [
      "catchball",
      "request-framing",
      "confidence-signal",
      "legitimacy"
    ],
    "synonyms": [
      "confidence level",
      "request firmness",
      "intent signal"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Catchball Loop State",
    "slug": "catchball-loop-state",
    "type": "flat_list",
    "status": "active",
    "tier": 2,
    "domain": "Catchball Governance, State Machine",
    "description": "The discrete state machine governing the lifecycle of a Catchball loop. Each state reflects whose turn it is and what has happened so far. Transitions are deterministic and driven by explicit actions.\n",
    "purpose": "Provide a precise, machine-readable representation of where a loop is so that UI, notifications, and workflow routing can respond correctly.\n",
    "dimensions": [
      {
        "slug": "state",
        "description": "current state (see values)"
      },
      {
        "slug": "entered_at",
        "description": "timestamp of state entry"
      },
      {
        "slug": "entered_by",
        "description": "who/what caused the transition"
      },
      {
        "slug": "previous_state",
        "description": "for audit trail"
      }
    ],
    "values": [
      {
        "slug": "OPEN",
        "description": "ball tossed; receiver has possession and must act"
      },
      {
        "slug": "PROPOSED",
        "description": "proposal ball returned; tossing tier is reviewing"
      },
      {
        "slug": "REVISION_REQUESTED",
        "description": "tossing tier requests changes; ball returns to proposer"
      },
      {
        "slug": "ACCEPTED",
        "description": "proposal accepted; loop ends; execution phase begins"
      },
      {
        "slug": "FINAL_REJECTED",
        "description": "proposal rejected definitively; loop ends; escalate or restart"
      }
    ],
    "transitions": [
      {
        "from": "OPEN",
        "to": "PROPOSED",
        "trigger": "Receiver composes and passes Proposal Ball"
      },
      {
        "from": "PROPOSED",
        "to": "ACCEPTED",
        "trigger": "Tossing tier approves proposal"
      },
      {
        "from": "PROPOSED",
        "to": "REVISION_REQUESTED",
        "trigger": "Tossing tier requests changes"
      },
      {
        "from": "PROPOSED",
        "to": "FINAL_REJECTED",
        "trigger": "Tossing tier definitively rejects"
      },
      {
        "from": "REVISION_REQUESTED",
        "to": "PROPOSED",
        "trigger": "Receiver addresses feedback and re-passes"
      },
      {
        "from": "REVISION_REQUESTED",
        "to": "FINAL_REJECTED",
        "trigger": "Max iterations reached or receiver withdraws"
      }
    ],
    "parent": "catchball-governance",
    "children": [],
    "tags": [
      "catchball",
      "state-machine",
      "loop-lifecycle"
    ],
    "synonyms": [
      "loop lifecycle state",
      "catchball state"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Catchball Loop Status",
    "slug": "catchball-loop-status",
    "type": "flat_list",
    "status": "draft",
    "tier": 2,
    "domain": "Catchball Governance, Operational Visibility",
    "description": "A coarse, high-level summary of a Catchball loop's operational condition. Distinct from Loop State (which is the precise machine state): Loop Status answers \"is this loop moving or stuck?\"\n",
    "purpose": "Give dashboards and admins an at-a-glance signal about loop health without requiring them to parse fine-grained state machines.\n",
    "dimensions": [
      {
        "slug": "status_type",
        "description": "see values"
      },
      {
        "slug": "blocker_ref",
        "description": "if blocked, link to the Catchball Blocker"
      },
      {
        "slug": "age_days",
        "description": "how long has it been in current status"
      }
    ],
    "values": [
      {
        "slug": "in_flight",
        "description": "ball is actively being worked; normal operation"
      },
      {
        "slug": "blocked",
        "description": "a named blocker is preventing advancement"
      }
    ],
    "parent": "catchball-governance",
    "children": [],
    "tags": [
      "catchball",
      "operational-visibility",
      "dashboard"
    ],
    "synonyms": [
      "loop health",
      "loop operational state"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Ball Waiting State",
    "slug": "ball-waiting-state",
    "type": "flat_list",
    "status": "active",
    "tier": 2,
    "domain": "Catchball Governance, Operational Visibility",
    "description": "Where a sent ball stands while it is away from the person who sent it, in plain words. Read from what the ball already carries (catchball_balls.ball_status, opened_at, due_date) — never stored.\n",
    "purpose": "Let the sender see at a glance whether the people below have opened the request, are working it, or have let it run past its due date, and drive the overdue nudges (owner ruling 2026-10-14, roadmap C-3).\n",
    "dimensions": [
      {
        "slug": "state",
        "description": "see values"
      },
      {
        "slug": "days",
        "description": "days since sent (sent), since opened (with_them), or since the due date (overdue)"
      },
      {
        "slug": "holder",
        "description": "the named recipient, else the org unit the ball was sent to"
      }
    ],
    "values": [
      {
        "slug": "sent",
        "description": "Sent — ball_status 'sent' and not opened yet"
      },
      {
        "slug": "with_them",
        "description": "With them — opened and being worked: 'sent' once opened, or opened, gathering_input, in_local_deliberation, proposal_drafted, returned_for_revision"
      },
      {
        "slug": "overdue",
        "description": "Overdue — either of the above with the due date (a calendar day) in the past; overdue from the day after"
      }
    ],
    "parent": "catchball-governance",
    "children": [],
    "tags": [
      "catchball",
      "operational-visibility",
      "notification"
    ],
    "synonyms": [
      "ball is away",
      "waiting on them"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Loop Intent",
    "slug": "loop-intent",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Catchball Governance, Request Payload",
    "description": "The structured payload carried by a Request Ball when it is tossed downward. Contains everything the receiving tier needs to understand the request, do judgment work, and compose a valid proposal.\n",
    "purpose": "Prevent ambiguous or under-specified requests from reaching lower tiers. Ensure the receiving tier has the context, constraints, and guidance needed to respond meaningfully.\n",
    "dimensions": [
      {
        "slug": "strategic_context",
        "description": "why this decision matters strategically"
      },
      {
        "slug": "asks",
        "description": "explicit questions or deliverables requested"
      },
      {
        "slug": "constraints",
        "description": "hard limits (budget, timeline, headcount, etc.)"
      },
      {
        "slug": "guardrails",
        "description": "lines that must not be crossed (non-negotiable)"
      },
      {
        "slug": "response_guidance",
        "description": "how to format or approach the response"
      },
      {
        "slug": "interpretation",
        "description": "receiver's reading of the request (filled in by receiver)"
      },
      {
        "slug": "due_date",
        "description": "when response is expected"
      },
      {
        "slug": "max_iterations",
        "description": "how many revision rounds are allowed"
      },
      {
        "slug": "clarifications",
        "description": "async Q&A thread (without ball movement)"
      },
      {
        "slug": "legitimacy_marking",
        "description": "draft | proposed | preliminary"
      }
    ],
    "parent": "catchball-governance",
    "children": [
      "loop-clarification-request"
    ],
    "tags": [
      "catchball",
      "request-payload",
      "loop-content"
    ],
    "synonyms": [
      "catchball request",
      "loop payload",
      "ball content"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Loop Readiness Gates",
    "slug": "loop-readiness-gates",
    "type": "toggle",
    "status": "draft",
    "tier": 2,
    "domain": "Catchball Governance, Readiness",
    "description": "A configurable checklist of named conditions that must be satisfied at a given tier before the ball can be advanced (passed to the next level). Gates are checked before the Proposal Ball is composed and passed.\n",
    "purpose": "Prevent premature ball advancement. Ensure judgment work is complete before passing to the next level. Make \"are we ready to pass?\" explicit and auditable.\n",
    "dimensions": [
      {
        "slug": "gate_name",
        "description": "e.g., interpretation_written, all_tracks_ready"
      },
      {
        "slug": "gate_status",
        "description": "met | unmet | waived"
      },
      {
        "slug": "required",
        "description": "is this gate mandatory or optional?"
      },
      {
        "slug": "tier",
        "description": "which level this gate applies to"
      },
      {
        "slug": "waived_by",
        "description": "who waived it and why (if waived)"
      }
    ],
    "values": [
      {
        "slug": "interpretation_written"
      },
      {
        "slug": "all_tracks_ready"
      },
      {
        "slug": "constraints_validated"
      },
      {
        "slug": "dependencies_mapped"
      },
      {
        "slug": "risks_identified"
      },
      {
        "slug": "resource_committed"
      },
      {
        "slug": "legal_review_complete",
        "description": "example of workspace-specific gate",
        "example": true
      },
      {
        "slug": "budget_alignment_confirmed"
      }
    ],
    "parent": "catchball-governance",
    "children": [],
    "tags": [
      "catchball",
      "readiness",
      "gate",
      "checklist"
    ],
    "synonyms": [
      "readiness checklist",
      "loop gates",
      "advancement conditions"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Catchball Blocker",
    "slug": "catchball-blocker",
    "type": "taxonomy",
    "status": "draft",
    "tier": 2,
    "domain": "Catchball Governance, Obstruction",
    "description": "A named, linked reason that is preventing a Catchball loop from advancing. Blockers are explicit: they name the problem, link to the affected gate or constraint, and must be resolved before the ball can move.\n",
    "purpose": "Make loop stalls visible and actionable. Prevent loops from being \"blocked\" for unknown reasons. Give executives and admins a clear diagnosis when a loop is stuck.\n",
    "dimensions": [
      {
        "slug": "blocker_type",
        "description": "see values"
      },
      {
        "slug": "description",
        "description": "human-readable explanation"
      },
      {
        "slug": "affected_gate",
        "description": "which Loop Readiness Gate triggered this"
      },
      {
        "slug": "resolution_path",
        "description": "how to resolve (escalate, clarify, revise)"
      },
      {
        "slug": "created_at",
        "description": "when blocker was identified"
      },
      {
        "slug": "resolved_at",
        "description": "when blocker was cleared (null if open)"
      }
    ],
    "values": [
      {
        "slug": "criteria_not_locked",
        "description": "evaluation criteria not yet finalized"
      },
      {
        "slug": "budget_misalignment",
        "description": "work proposal exceeds budget constraint"
      },
      {
        "slug": "dependency_external",
        "description": "blocked on external team/org"
      },
      {
        "slug": "interpretation_unclear",
        "description": "receiver doesn't understand the request"
      },
      {
        "slug": "team_capacity_insufficient",
        "description": "IC teams cannot commit to timeline"
      },
      {
        "slug": "conflicting_guardrails",
        "description": "two guardrails contradict each other"
      },
      {
        "slug": "escalation_needed",
        "description": "exceeds receiver's authority"
      },
      {
        "slug": "missing_decision_rights",
        "description": "no one has authority to proceed"
      },
      {
        "slug": "missing_stakeholder_input",
        "description": "required input hasn't been provided"
      }
    ],
    "parent": "catchball-governance",
    "children": [],
    "tags": [
      "catchball",
      "obstruction",
      "diagnostic",
      "unblocking"
    ],
    "synonyms": [
      "loop impediment",
      "stall reason",
      "advancement blocker"
    ]
  },
  {
    "family": "Governance & Authority",
    "name": "Loop Clarification Request",
    "slug": "loop-clarification-request",
    "type": "hybrid",
    "status": "draft",
    "tier": 3,
    "domain": "Catchball Governance, Async Communication",
    "description": "An asynchronous question-and-answer thread raised within a Catchball loop that does NOT move the ball. Used when the receiving tier needs clarification from the tossing tier without formally returning the ball.\n",
    "purpose": "Allow communication and clarification to happen without disrupting ball possession. Prevent confusion between \"I'm asking a question\" (no ball movement) and \"I'm requesting a revision\" (ball moves back).\n",
    "dimensions": [
      {
        "slug": "id",
        "description": "unique identifier"
      },
      {
        "slug": "question",
        "description": "the question being asked"
      },
      {
        "slug": "asked_by",
        "description": "user who asked"
      },
      {
        "slug": "asked_at",
        "description": "timestamp"
      },
      {
        "slug": "response",
        "description": "answer from tossing tier"
      },
      {
        "slug": "responded_by",
        "description": "who responded"
      },
      {
        "slug": "responded_at",
        "description": "when they responded"
      },
      {
        "slug": "loop_id",
        "description": "which loop this belongs to"
      }
    ],
    "values": [],
    "parent": "loop-intent",
    "children": [],
    "tags": [
      "catchball",
      "async-communication",
      "clarification",
      "no-ball-movement"
    ],
    "synonyms": [
      "catchball question",
      "in-loop question",
      "clarification thread"
    ]
  },
  {
    "family": "Item & Work",
    "name": "Item Lifecycle / Status",
    "slug": "item-lifecycle-status",
    "type": "flat_list",
    "status": "active",
    "tier": 1,
    "domain": "Work Maturation, Item Management",
    "description": "The global maturation state of a prioritizable work item. Describes how fully understood and specified the item is, independent of any particular cycle, session, or context. An item is in exactly one lifecycle state at a time.\n",
    "purpose": "Prevent fuzzy, under-specified ideas from being prioritized alongside well-defined work. Give teams a clear workflow for developing items from concept to ready-for-execution. Answer \"is this item mature enough to use?\"\n",
    "dimensions": [
      {
        "slug": "stage",
        "description": "current lifecycle stage (see values)"
      },
      {
        "slug": "entered_at",
        "description": "when item entered this stage"
      },
      {
        "slug": "definition_criteria",
        "description": "what must be true to advance to the next stage"
      }
    ],
    "values": [
      {
        "enriching": "Building out detail. Moving from vague idea to understood concept. Key questions: What is this? Who cares? Why? Exit condition: Description clear, basic metadata filled, dependencies noted.\n"
      },
      {
        "qualifying": "Testing viability. Moving from understood concept to validated feasibility. Key questions: Is this feasible? What are the trade-offs? How much will it cost? Exit condition: Constraints known, effort estimated, viability confirmed.\n"
      },
      {
        "defining": "Locking down specifics. Moving from viable to commitment-ready. Key questions: What exactly? How do we measure success? Who commits? Exit condition: Requirements locked, success metrics defined, team committed.\n"
      }
    ],
    "parent": null,
    "children": [
      "item-readiness-status-contextual",
      "work-type-register",
      "source-type-register",
      "decision-unit"
    ],
    "tags": [
      "item",
      "lifecycle",
      "maturation",
      "readiness-gate",
      "workflow-routing"
    ],
    "synonyms": [
      "item status",
      "item maturity",
      "item stage",
      "item readiness (global)"
    ]
  },
  {
    "family": "Item & Work",
    "name": "Item Readiness Status (Contextual)",
    "slug": "item-readiness-status-contextual",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Work Readiness, Cycle/Session Context",
    "description": "Whether a work item is ready to be used in a specific context (a cycle, session, track, or tool) — independent of its global lifecycle stage. Named blockers explain exactly what is preventing use.\n",
    "purpose": "Answer \"can I use this item *here, right now*?\" with precision. Allow globally mature items to be held back from specific contexts by explicit, named reasons — rather than vague \"not ready\" messages.\n",
    "dimensions": [
      {
        "slug": "item_id",
        "description": "the item being assessed"
      },
      {
        "slug": "context_type",
        "description": "cycle | session | track | tool"
      },
      {
        "slug": "context_id",
        "description": "the specific cycle/session/track"
      },
      {
        "slug": "readiness_state",
        "description": "ready | not_ready | conditional",
        "values": [
          {
            "slug": "ready",
            "description": "no blockers; item can be used in this context"
          },
          {
            "slug": "not_ready",
            "description": "one or more named blockers prevent use"
          },
          {
            "slug": "conditional",
            "description": "ready if a condition is met (e.g., after another item ships)"
          }
        ]
      },
      {
        "slug": "blockers",
        "description": "list of named blockers (see values)",
        "values": [
          {
            "slug": "missing_value_proposal",
            "description": "no effort or impact estimate"
          },
          {
            "slug": "missing_success_metric",
            "description": "no success criteria defined"
          },
          {
            "slug": "unresolved_dependency",
            "description": "depends on another item not yet shipped"
          },
          {
            "slug": "criteria_mismatch",
            "description": "item does not fit the session's evaluation criteria"
          },
          {
            "slug": "scope_out_of_bounds",
            "description": "item is out of scope for this cycle"
          },
          {
            "slug": "stakeholder_not_aligned",
            "description": "key stakeholder has not reviewed or approved"
          },
          {
            "slug": "duplicate_pending",
            "description": "possible duplicate under review"
          }
        ]
      },
      {
        "slug": "conditions",
        "description": "if conditional, what must be true to become ready"
      }
    ],
    "parent": "item-lifecycle-status",
    "children": [],
    "tags": [
      "item",
      "readiness",
      "contextual",
      "blocker",
      "gate"
    ],
    "synonyms": [
      "contextual readiness",
      "item eligibility",
      "item suitability"
    ]
  },
  {
    "family": "Item & Work",
    "name": "Work Type Register",
    "slug": "work-type-register",
    "type": "taxonomy",
    "status": "draft",
    "tier": 2,
    "domain": "Work Classification, Planning",
    "description": "A categorization of the kind of work an item represents. Work type determines which planning/execution recipe applies, what qualification criteria are relevant, and how effort and risk are estimated.\n",
    "purpose": "Prevent treating all work as equivalent. Different types of work (a new product build vs. a bug fix vs. an optimization) require different processes, team compositions, and risk models.\n",
    "dimensions": [
      {
        "slug": "work_type",
        "description": "see values"
      },
      {
        "slug": "approach_kind",
        "description": "how this kind of work is carried out (canon 111 §2.2)"
      }
    ],
    "values": [
      {
        "slug": "novel_build",
        "name": "Novel Build",
        "approach_kind": "experimental"
      },
      {
        "slug": "bug_fix",
        "name": "Bug Fix",
        "approach_kind": "diagnostic"
      },
      {
        "slug": "tech_debt",
        "name": "Technical Debt Retirement",
        "approach_kind": "iterative"
      },
      {
        "slug": "safe_feature",
        "name": "Safe / Incremental Feature",
        "approach_kind": "incremental"
      },
      {
        "slug": "operational",
        "name": "Operational Work",
        "approach_kind": "runbook"
      },
      {
        "slug": "venture_bet",
        "name": "Venture Bet",
        "approach_kind": "validating"
      },
      {
        "slug": "known_replication",
        "name": "Known Solution Replication",
        "approach_kind": "replicative"
      }
    ],
    "parent": "item-lifecycle-status",
    "children": [],
    "tags": [
      "item",
      "work-classification",
      "planning",
      "recipe"
    ],
    "synonyms": [
      "item type",
      "work category",
      "work classification"
    ]
  },
  {
    "family": "Item & Work",
    "name": "Source Type Register",
    "slug": "source-type-register",
    "type": "taxonomy",
    "status": "draft",
    "tier": 2,
    "domain": "Provenance, Item Origin",
    "description": "The origin or provenance of a work item. Where it came from shapes how it should be enriched, who the key stakeholders are, and what weight or urgency it may carry.\n",
    "purpose": "Enable traceability from finished work back to its origin. Inform stakeholder identification, weighting in evaluation, and communication. Distinguish demand-driven from strategy-driven work.\n",
    "dimensions": [
      {
        "slug": "source_type",
        "description": "see values"
      },
      {
        "slug": "source_owner",
        "description": "person/group who submitted or originated the item"
      },
      {
        "slug": "source_date",
        "description": "when it was submitted"
      },
      {
        "slug": "source_reference",
        "description": "link to original ticket, email, meeting, etc."
      }
    ],
    "values": [
      {
        "slug": "sales_request",
        "typical_urgency": "high",
        "key_stakeholder": "account_manager"
      },
      {
        "slug": "customer_council",
        "typical_urgency": "medium_to_high",
        "key_stakeholder": "product_manager"
      },
      {
        "slug": "strategic_initiative",
        "typical_urgency": "variable",
        "key_stakeholder": "executive"
      },
      {
        "slug": "internal_idea",
        "typical_urgency": "low",
        "key_stakeholder": "submitter"
      },
      {
        "slug": "regulatory_requirement",
        "typical_urgency": "high",
        "key_stakeholder": "legal_compliance"
      },
      {
        "slug": "technical_debt",
        "typical_urgency": "variable",
        "key_stakeholder": "engineering_lead"
      },
      {
        "slug": "partner_request",
        "typical_urgency": "medium",
        "key_stakeholder": "partnerships_lead"
      }
    ],
    "parent": "item-lifecycle-status",
    "children": [],
    "tags": [
      "item",
      "provenance",
      "origin",
      "traceability"
    ],
    "synonyms": [
      "item origin",
      "demand source",
      "request origin"
    ]
  },
  {
    "family": "Item & Work",
    "name": "Decision Unit",
    "slug": "decision-unit",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Item Aggregation, Runtime Readiness",
    "description": "A computed, unified view of an item's accumulated state across all relevant processes: its lifecycle stage, contextual readiness, session participation history, track membership, Catchball involvement, and provenance. A runtime object, not a new database table.\n",
    "purpose": "Answer \"is this item ready to be acted on right now?\" by aggregating state from multiple Registers into one coherent view. Prevent users from needing to check lifecycle, then readiness, then session history separately.\n",
    "dimensions": [
      {
        "slug": "item_id",
        "description": "the item being described"
      },
      {
        "slug": "lifecycle_stage",
        "description": "from Item Lifecycle/Status"
      },
      {
        "slug": "contextual_readiness",
        "description": "from Item Readiness Status for current context"
      },
      {
        "slug": "session_history",
        "description": "has this been evaluated in a session? result?"
      },
      {
        "slug": "track_membership",
        "description": "which tracks include this item?"
      },
      {
        "slug": "catchball_loops",
        "description": "has this item been part of a Catchball loop?"
      },
      {
        "slug": "provenance_events",
        "description": "significant events in this item's history"
      },
      {
        "slug": "computed_at",
        "description": "when this view was generated"
      }
    ],
    "value_groups": {
      "overall_readiness": [
        {
          "slug": "ready_to_prioritize",
          "description": "lifecycle=defining AND contextual_readiness=ready"
        },
        {
          "slug": "needs_definition",
          "description": "lifecycle=enriching or qualifying"
        },
        {
          "slug": "contextually_blocked",
          "description": "lifecycle=defining but contextual blockers exist"
        },
        {
          "slug": "pending_evaluation",
          "description": "in a live session; result pending"
        },
        {
          "slug": "evaluated",
          "description": "has session result; rank available"
        },
        {
          "slug": "committed",
          "description": "part of an accepted proposal/POR"
        }
      ]
    },
    "parent": "item-lifecycle-status",
    "children": [],
    "tags": [
      "item",
      "aggregation",
      "runtime-view",
      "readiness",
      "decision-support"
    ],
    "synonyms": [
      "item state summary",
      "item readiness view",
      "item decision context"
    ]
  },
  {
    "family": "Process & Session",
    "name": "Session Type / Decision Class Mapping",
    "slug": "session-type-decision-class-mapping",
    "type": "matrix",
    "status": "draft",
    "tier": 1,
    "domain": "Session Design, Tool Selection",
    "description": "A mapping between classes of decisions (what kind of choice is being made) and the types of sessions and tools that are appropriate for making them. The primary rule that determines which tools are offered in a given context.\n",
    "purpose": "Prevent tool-decision mismatches (using a tournament for a simple yes/no; using a dot-vote for a rigorous multi-criteria evaluation). Make tool recommendation systematic rather than ad hoc.\n",
    "dimensions": [
      {
        "slug": "decision_class",
        "description": "what category of decision (e.g., Strategic Priority, Vendor Selection)",
        "values": [
          {
            "slug": "strategic_prioritization",
            "description": "long-term bets; multi-stakeholder"
          },
          {
            "slug": "quarterly_planning",
            "description": "near-term commitment; team-level"
          },
          {
            "slug": "vendor_or_partner_selection",
            "description": "comparative evaluation of external options"
          },
          {
            "slug": "feature_triage",
            "description": "quick in-vs-out decisions on backlog items"
          },
          {
            "slug": "risk_assessment",
            "description": "ranking by risk or threat"
          },
          {
            "slug": "resource_allocation",
            "description": "distributing limited budget/capacity"
          },
          {
            "slug": "idea_generation_ranking",
            "description": "bottom-up innovation prioritization"
          }
        ]
      },
      {
        "slug": "session_type",
        "description": "which session types are appropriate"
      },
      {
        "slug": "recommended_tools",
        "description": "list of tools well-suited to this decision class"
      },
      {
        "slug": "excluded_tools",
        "description": "tools explicitly unsuitable"
      },
      {
        "slug": "participation_mode",
        "description": "individual | collaborative | facilitated"
      },
      {
        "slug": "output_shape",
        "description": "ranked list | selected set | scored matrix | yes/no",
        "values": [
          {
            "slug": "ranked_list",
            "description": "items in ordered sequence"
          },
          {
            "slug": "selected_set",
            "description": "subset chosen from pool"
          },
          {
            "slug": "scored_matrix",
            "description": "items with numeric scores per criterion"
          },
          {
            "slug": "binary_decision",
            "description": "single yes/no output"
          },
          {
            "slug": "weighted_allocation",
            "description": "percentage/points distributed across items"
          }
        ]
      }
    ],
    "parent": null,
    "children": [
      "tool-recommendation-matrix",
      "session-state",
      "session-participant-roles"
    ],
    "tags": [
      "session",
      "tool-selection",
      "decision-type",
      "recommendation"
    ],
    "synonyms": [
      "decision-to-tool mapping",
      "session type mapping",
      "tool fit matrix"
    ]
  },
  {
    "family": "Process & Session",
    "name": "Tool Recommendation Matrix",
    "slug": "tool-recommendation-matrix",
    "type": "matrix",
    "status": "draft",
    "tier": 2,
    "domain": "Tool Selection, Session Design",
    "description": "A computed recommendation of which specific tools are most suitable, given a decision class, the item pool characteristics (work types, count, maturity), workspace configuration, and output shape desired.\n",
    "purpose": "Remove guesswork from tool selection. Provide principled, context-aware recommendations rather than forcing users to know all available tools and their trade-offs.\n",
    "dimensions": [
      {
        "slug": "decision_class",
        "description": "from Session Type / Decision Class Mapping"
      },
      {
        "slug": "item_count",
        "description": "number of items in pool"
      },
      {
        "slug": "item_types",
        "description": "mix of Work Types in pool"
      },
      {
        "slug": "output_shape",
        "description": "desired output type"
      },
      {
        "slug": "time_available",
        "description": "available session time"
      },
      {
        "slug": "workspace_config",
        "description": "enabled modules and feature flags"
      },
      {
        "slug": "recommended",
        "description": "list of recommended tools (ranked)"
      },
      {
        "slug": "not_recommended",
        "description": "list of unsuitable tools with rationale"
      }
    ],
    "value_groups": {
      "tool_names": [
        {
          "slug": "rice_scoring"
        },
        {
          "slug": "impact_effort_matrix"
        },
        {
          "slug": "ahp"
        },
        {
          "slug": "pairwise_ranking"
        },
        {
          "slug": "dot_vote"
        },
        {
          "slug": "moscow"
        },
        {
          "slug": "weighted_scoring_matrix"
        },
        {
          "slug": "custom_2x2_matrix"
        },
        {
          "slug": "max_priorities_pyramid"
        },
        {
          "slug": "stack_ranking"
        }
      ]
    },
    "parent": "session-type-decision-class-mapping",
    "children": [],
    "tags": [
      "tool",
      "recommendation",
      "session-design",
      "context-aware"
    ],
    "synonyms": [
      "tool selector",
      "tool fit engine",
      "tool suggestion"
    ]
  },
  {
    "family": "Process & Session",
    "name": "Session State",
    "slug": "session-state",
    "type": "flat_list",
    "status": "draft",
    "tier": 2,
    "domain": "Session Lifecycle",
    "description": "The discrete lifecycle state of a prioritization session. Determines what actions are available to participants and facilitators, and what UI is shown.\n",
    "purpose": "Give sessions a deterministic lifecycle so that UI, permissions, and notifications respond correctly at each phase. Prevent operations that are invalid at the current stage (e.g., adding items after scoring begins).\n",
    "dimensions": [
      {
        "slug": "state",
        "description": "current state (see values)"
      },
      {
        "slug": "session_type",
        "description": "live | async | hybrid"
      },
      {
        "slug": "entered_at",
        "description": "timestamp of state entry"
      }
    ],
    "values": [
      {
        "slug": "setup",
        "allowed_actions": [
          "edit_config",
          "add_items",
          "set_criteria",
          "invite_participants"
        ],
        "blocked_actions": [
          "score",
          "submit_result"
        ]
      },
      {
        "slug": "open_for_participants",
        "allowed_actions": [
          "join",
          "view_items",
          "ask_questions"
        ],
        "blocked_actions": [
          "score",
          "submit_result",
          "edit_config"
        ]
      },
      {
        "slug": "live",
        "allowed_actions": [
          "score",
          "comment",
          "view_results_partial"
        ],
        "blocked_actions": [
          "add_items",
          "edit_config",
          "remove_participants"
        ]
      },
      {
        "slug": "paused",
        "allowed_actions": [
          "comment",
          "view"
        ],
        "blocked_actions": [
          "score"
        ]
      },
      {
        "slug": "completed",
        "allowed_actions": [
          "review_results",
          "submit_result"
        ],
        "blocked_actions": [
          "score",
          "edit_config"
        ]
      },
      {
        "slug": "published",
        "allowed_actions": [
          "view",
          "export"
        ],
        "blocked_actions": [
          "score",
          "edit_config",
          "submit_result"
        ]
      }
    ],
    "parent": "session-type-decision-class-mapping",
    "children": [],
    "tags": [
      "session",
      "lifecycle",
      "state-machine"
    ],
    "synonyms": [
      "session lifecycle",
      "session phase",
      "session status"
    ]
  },
  {
    "family": "Process & Session",
    "name": "Session Participant Roles",
    "slug": "session-participant-roles",
    "type": "taxonomy",
    "status": "draft",
    "tier": 2,
    "domain": "Session Execution, Role Assignment",
    "description": "The named functional roles available to participants within a session. Roles determine what actions each person can take and what they can see.\n",
    "purpose": "Prevent role confusion in sessions. Make clear who is directing, who is judging, and who is observing. Enable role-based permission checks.\n",
    "dimensions": [
      {
        "slug": "role_name",
        "description": "see values"
      },
      {
        "slug": "capabilities",
        "description": "what this role can do"
      },
      {
        "slug": "count_requirement",
        "description": "how many of this role are required/optional"
      },
      {
        "slug": "assignment",
        "description": "user(s) assigned to this role"
      }
    ],
    "values": [
      {
        "slug": "facilitator",
        "description": "Directs the session; controls pacing; resolves scoring disputes",
        "capabilities": [
          "start_session",
          "pause_session",
          "close_session",
          "override_score",
          "add_item_during_live"
        ],
        "count": "exactly_one"
      },
      {
        "slug": "evaluator",
        "description": "Scores items against the criteria; primary decision-maker",
        "capabilities": [
          "score",
          "comment",
          "view_results"
        ],
        "count": "one_or_more"
      },
      {
        "slug": "worker",
        "description": "Provides input or context without scoring authority",
        "capabilities": [
          "comment",
          "provide_estimates",
          "answer_questions"
        ],
        "count": "zero_or_more"
      },
      {
        "slug": "observer",
        "description": "Watches the session without participating",
        "capabilities": [
          "view"
        ],
        "count": "zero_or_more"
      }
    ],
    "parent": "session-type-decision-class-mapping",
    "children": [],
    "tags": [
      "session",
      "role",
      "participation",
      "access-control"
    ],
    "synonyms": [
      "session role",
      "participant type",
      "session function"
    ]
  },
  {
    "family": "Process & Session",
    "name": "Criteria Set (Versioned)",
    "slug": "criteria-set-versioned",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Session Execution, Evaluation",
    "description": "A named, versioned, and locked set of weighted evaluation criteria used within a track or session. Once locked, criteria cannot change mid-session. Versions are archived for audit and result traceability.\n",
    "purpose": "Ensure that all items in a session are evaluated against the same criteria, with the same weights, at the same point in time. Enable \"why did this item rank here?\" to be answerable by pointing to the locked criteria.\n",
    "dimensions": [
      {
        "slug": "criteria_set_id",
        "description": "unique identifier"
      },
      {
        "slug": "version",
        "description": "version number (integer or semver)"
      },
      {
        "slug": "name",
        "description": "human-readable label"
      },
      {
        "slug": "status",
        "description": "draft | active | locked | archived",
        "values": [
          {
            "slug": "draft",
            "description": "being designed; not yet in use"
          },
          {
            "slug": "active",
            "description": "in use for scoring; can still be modified"
          },
          {
            "slug": "locked",
            "description": "locked for session; no modifications allowed"
          },
          {
            "slug": "archived",
            "description": "historical; not in active use"
          }
        ]
      },
      {
        "slug": "criteria",
        "description": "list of individual criteria (see below)"
      },
      {
        "slug": "locked_at",
        "description": "when criteria were locked for this session"
      },
      {
        "slug": "locked_by",
        "description": "who locked them"
      }
    ],
    "parent": null,
    "children": [],
    "tags": [
      "session",
      "evaluation",
      "criteria",
      "versioning",
      "audit"
    ],
    "synonyms": [
      "evaluation criteria",
      "scoring criteria",
      "rubric"
    ]
  },
  {
    "family": "Process & Session",
    "name": "Score Divergence",
    "slug": "score-divergence",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Session Execution, Evaluation Quality",
    "description": "A detected condition where one or more evaluators scored an item significantly differently from the group. Includes a diagnosis of why the divergence likely occurred and a recommended resolution path.\n",
    "purpose": "Surface scoring disagreements that might indicate genuine differences in understanding, information, or values — so they can be discussed rather than averaged away silently.\n",
    "dimensions": [
      {
        "slug": "item_id",
        "description": "which item shows divergence"
      },
      {
        "slug": "criterion",
        "description": "which criterion shows divergence"
      },
      {
        "slug": "outlier_evaluator",
        "description": "who scored differently"
      },
      {
        "slug": "outlier_score",
        "description": "what they scored"
      },
      {
        "slug": "group_median",
        "description": "what the rest of the group scored"
      },
      {
        "slug": "divergence_magnitude",
        "description": "how large the gap is (absolute or relative)"
      },
      {
        "slug": "diagnosis",
        "description": "LLM-generated explanation of likely cause"
      },
      {
        "slug": "resolution",
        "description": "discussion | re_score | accept_outlier | facilitator_override",
        "values": [
          {
            "slug": "discussion",
            "description": "surfaced for group discussion; may or may not change"
          },
          {
            "slug": "re_score",
            "description": "evaluator changes score after discussion"
          },
          {
            "slug": "accept_outlier",
            "description": "group agrees divergence is valid; keep both"
          },
          {
            "slug": "facilitator_override",
            "description": "facilitator makes final call"
          }
        ]
      }
    ],
    "parent": null,
    "children": [],
    "tags": [
      "session",
      "scoring",
      "disagreement",
      "diagnosis",
      "quality"
    ],
    "synonyms": [
      "scoring outlier",
      "score disagreement",
      "evaluation divergence"
    ]
  },
  {
    "family": "Process & Session",
    "name": "Published Priority List",
    "slug": "published-priority-list",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Session Output, Cycle Artifact",
    "description": "The durable, immutable output of a track's cycle: an ordered list of prioritized items with rationale, plus explicitly excluded items with reasons for exclusion.\n",
    "purpose": "Provide a stable, traceable record of \"what we decided to do and why.\" Enable teams to refer back to decisions, understand what was included vs. excluded, and audit the basis for prioritization.\n",
    "dimensions": [
      {
        "slug": "list_id",
        "description": "unique identifier"
      },
      {
        "slug": "cycle_id",
        "description": "which cycle this belongs to"
      },
      {
        "slug": "track_id",
        "description": "which track produced this"
      },
      {
        "slug": "status",
        "description": "draft | published | superseded",
        "values": [
          {
            "slug": "draft",
            "description": "in progress; not yet committed"
          },
          {
            "slug": "published",
            "description": "immutable; official output"
          },
          {
            "slug": "superseded",
            "description": "replaced by a newer version"
          }
        ]
      },
      {
        "slug": "items_included",
        "description": "ordered list of items with rank and rationale"
      },
      {
        "slug": "items_excluded",
        "description": "list of excluded items with exclusion reason"
      },
      {
        "slug": "criteria_set_ref",
        "description": "which Criteria Set (Versioned) was used"
      },
      {
        "slug": "session_refs",
        "description": "which sessions contributed to this list"
      },
      {
        "slug": "published_at",
        "description": "when it became immutable"
      },
      {
        "slug": "published_by",
        "description": "who published it"
      }
    ],
    "value_groups": {
      "exclusion_reason": [
        {
          "slug": "too_expensive",
          "description": "effort exceeds available capacity"
        },
        {
          "slug": "out_of_scope",
          "description": "doesn't fit cycle focus"
        },
        {
          "slug": "blocked_dependency",
          "description": "depends on work not yet ready"
        },
        {
          "slug": "deferred_to_next",
          "description": "valid but pushed to next cycle"
        },
        {
          "slug": "rejected",
          "description": "evaluated and actively not chosen"
        }
      ]
    },
    "parent": null,
    "children": [],
    "tags": [
      "cycle",
      "output",
      "artifact",
      "audit",
      "traceability"
    ],
    "synonyms": [
      "priority list",
      "cycle output",
      "ranked backlog",
      "cycle result"
    ]
  },
  {
    "family": "Process & Session",
    "name": "Notification Event Type",
    "slug": "notification-event-type",
    "type": "taxonomy",
    "status": "active",
    "tier": 2,
    "domain": "Notifications, System Events",
    "description": "A named category of event that triggers a user notification. Events are produced by state transitions, administrative actions, or explicit user actions, and are routed to relevant recipients based on role and context.\n",
    "purpose": "Ensure stakeholders know what happened, when, and what to do next. Prevent missed handoffs (e.g., ball tossed but Middle doesn't know). Enable \"notification audit\" — what events fired and to whom?\n",
    "dimensions": [
      {
        "slug": "event_type",
        "description": "the specific notification event; synced from the NotificationEvent union in src/lib/notificationService.ts",
        "values": [
          {
            "slug": "session_scheduled",
            "description": "A session was scheduled; participants notified",
            "category": "session"
          },
          {
            "slug": "session_updated",
            "description": "Session details changed; participants notified",
            "category": "session"
          },
          {
            "slug": "session_cancelled",
            "description": "Session cancelled; participants notified",
            "category": "session"
          },
          {
            "slug": "session_starting_soon",
            "description": "Session starts soon; participants reminded",
            "category": "session"
          },
          {
            "slug": "session_started",
            "description": "Session is live; participants notified",
            "category": "session"
          },
          {
            "slug": "session_completed",
            "description": "Session done; stakeholders notified",
            "category": "session"
          },
          {
            "slug": "session_results_published",
            "description": "Priority list published; stakeholders notified",
            "category": "session"
          },
          {
            "slug": "cycle_created",
            "description": "A prioritization cycle was created (create_cycle, migration 762 trigger); workspace admins are told",
            "category": "cycle"
          },
          {
            "slug": "cycle_phase_changed",
            "description": "Cycle advanced to a new phase",
            "category": "cycle"
          },
          {
            "slug": "cycle_launched",
            "description": "A cycle design was launched (launch_cycle_design, migration 750 trigger); the cycle's owner, participants and loop participants are told",
            "category": "cycle"
          },
          {
            "slug": "cycle_completed",
            "description": "Cycle finished",
            "category": "cycle"
          },
          {
            "slug": "cycle_priorities_published",
            "description": "Cycle priorities published",
            "category": "cycle"
          },
          {
            "slug": "task_assigned",
            "description": "A task was assigned; assignee notified",
            "category": "task"
          },
          {
            "slug": "task_reminder",
            "description": "Reminder for an open task",
            "category": "task"
          },
          {
            "slug": "task_closing_soon",
            "description": "Task deadline approaching",
            "category": "task"
          },
          {
            "slug": "task_closed",
            "description": "Task closed",
            "category": "task"
          },
          {
            "slug": "task_results_ready",
            "description": "Task results available",
            "category": "task"
          },
          {
            "slug": "meeting_scheduled",
            "description": "Meeting scheduled; attendees notified",
            "category": "meeting"
          },
          {
            "slug": "meeting_updated",
            "description": "Meeting details changed",
            "category": "meeting"
          },
          {
            "slug": "meeting_cancelled",
            "description": "Meeting cancelled",
            "category": "meeting"
          },
          {
            "slug": "meeting_reminder",
            "description": "Meeting reminder",
            "category": "meeting"
          },
          {
            "slug": "steward_assigned",
            "description": "Item owner assigned; owner notified",
            "category": "item"
          },
          {
            "slug": "item_state_changed",
            "description": "Item state transitioned",
            "category": "item"
          },
          {
            "slug": "item_ready_for_ranking",
            "description": "Item became ready for ranking",
            "category": "item"
          },
          {
            "slug": "item_deprioritized",
            "description": "Item was deprioritized",
            "category": "item"
          },
          {
            "slug": "catchball_ball_tossed_to_middle",
            "description": "Executive tossed ball; Middle notified",
            "category": "catchball"
          },
          {
            "slug": "catchball_ball_tossed_to_bottom",
            "description": "Ball tossed to bottom tier; receivers notified",
            "category": "catchball"
          },
          {
            "slug": "catchball_ball_returned",
            "description": "Ball returned to the sender",
            "category": "catchball"
          },
          {
            "slug": "catchball_level_involved",
            "description": "A level was brought into the loop",
            "category": "catchball"
          },
          {
            "slug": "catchball_proposal_submitted",
            "description": "Proposal submitted upward",
            "category": "catchball"
          },
          {
            "slug": "catchball_proposal_accepted",
            "description": "Proposal accepted; parties notified",
            "category": "catchball"
          },
          {
            "slug": "catchball_proposal_returned",
            "description": "Middle passed proposal; Executive notified",
            "category": "catchball"
          },
          {
            "slug": "catchball_proposal_rejected",
            "description": "Proposal rejected; sender notified",
            "category": "catchball"
          },
          {
            "slug": "reminder",
            "description": "Generic reminder",
            "category": "general"
          },
          {
            "slug": "mention",
            "description": "User was mentioned",
            "category": "general"
          },
          {
            "slug": "comment_reply",
            "description": "Someone answered in a thread the user wrote in",
            "category": "general"
          },
          {
            "slug": "assignment",
            "description": "Generic assignment notification",
            "category": "general"
          },
          {
            "slug": "custom",
            "description": "Custom/ad-hoc notification",
            "category": "general"
          },
          {
            "slug": "catchball_ball_received",
            "description": "A ball (including a fan-out child ball) arrived for its recipient",
            "category": "catchball"
          },
          {
            "slug": "catchball_ball_claimed",
            "description": "The recipient took the ball; the sender / requester is told",
            "category": "catchball"
          },
          {
            "slug": "catchball_ball_released",
            "description": "The holder put the ball down; the people at the level where it now sits are told it is free (850)",
            "category": "catchball"
          },
          {
            "slug": "catchball_loop_closed",
            "description": "A loop closed; everyone with standing in it is told",
            "category": "catchball"
          },
          {
            "slug": "catchball_another_round",
            "description": "Another round was requested or opened on a loop",
            "category": "catchball"
          },
          {
            "slug": "catchball_ball_overdue",
            "description": "A ball is past its due date and still with the people it was sent to; they are nudged the day after, then weekly (ambient job catchball_overdue, migration 880)",
            "category": "catchball"
          },
          {
            "slug": "catchball_ball_overdue_sender",
            "description": "A ball the person sent went overdue; told once per due date (ambient job catchball_overdue, migration 880)",
            "category": "catchball"
          },
          {
            "slug": "v2mom_asked",
            "description": "Someone asked the person to write their V2MOM against a published one, by a date (ask_for_v2moms, 945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_ask_withdrawn",
            "description": "An ask to write a V2MOM was withdrawn; the person asked is told (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_submitted",
            "description": "A V2MOM was sent for review; the reviewer (owner of the V2MOM it aligns to) is told (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_submission_withdrawn",
            "description": "The author took a V2MOM back from review; the reviewer is told (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_approved",
            "description": "The reviewer approved a V2MOM; it is published and the author is told (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_returned",
            "description": "The reviewer returned a V2MOM to its author with a note (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_published",
            "description": "A V2MOM was published; the owners of the V2MOMs written against it are told (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_reopen_requested",
            "description": "The author asked to reopen a published or locked V2MOM for a mid-period change; the reviewer is told (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_reopened",
            "description": "The reviewer reopened a published or locked V2MOM; the author is told (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_reopen_declined",
            "description": "The reviewer declined a request to reopen a V2MOM; the person who asked is told (945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_ask_overdue",
            "description": "The V2MOM the person was asked to write is past its due date; nudged the day after, then weekly (ambient job v2mom_overdue, 945)",
            "category": "strategy"
          },
          {
            "slug": "v2mom_ask_overdue_asker",
            "description": "A V2MOM the person asked for went overdue; told once per due date (ambient job v2mom_overdue, 945)",
            "category": "strategy"
          },
          {
            "slug": "participation_requested",
            "description": "A planning participation request was sent to its named owner",
            "category": "admin"
          },
          {
            "slug": "participation_responded",
            "description": "A participation request was answered; the requester is told",
            "category": "admin"
          },
          {
            "slug": "participation_withdrawn",
            "description": "A participation request was withdrawn; the owner is told",
            "category": "admin"
          },
          {
            "slug": "plan_of_record_authorized",
            "description": "A Plan of Record was authorized; admins and plan participants are told",
            "category": "cycle"
          },
          {
            "slug": "structure_reorg_authorized",
            "description": "An org or product structure revision was authorized; admins are told",
            "category": "admin"
          },
          {
            "slug": "item_pool_published",
            "description": "A list was published; the cycle owner behind it is told",
            "category": "item"
          },
          {
            "slug": "idea_submitted",
            "description": "A new idea reached intake; workspace admins are told",
            "category": "item"
          },
          {
            "slug": "condition_broken",
            "description": "A condition stopped holding; the decision or plan owner is told",
            "category": "cycle"
          },
          {
            "slug": "commitment_term_tripped",
            "description": "A kill criterion, review trigger or expiry tripped; the plan owner is told",
            "category": "cycle"
          },
          {
            "slug": "decision_right_delegated",
            "description": "A decision right was delegated; the delegate is told",
            "category": "admin"
          },
          {
            "slug": "track_lock_engaged",
            "description": "A track boundary (participation, criteria, values, ranges) was locked; the track owner, open-session facilitators and (750) the track's participants are told",
            "category": "admin"
          },
          {
            "slug": "track_lock_released",
            "description": "A track boundary was unlocked; the track owner, open-session facilitators and (750) the track's participants are told",
            "category": "admin"
          },
          {
            "slug": "track_created",
            "description": "A track was added to a cycle (create_track, Launch or Generate tracks — migration 762 trigger); the cycle owner is told",
            "category": "cycle"
          },
          {
            "slug": "track_bound_to_cycle",
            "description": "A standalone track was bound to a cycle; the cycle owner is told",
            "category": "cycle"
          },
          {
            "slug": "session_linked_to_track",
            "description": "A session was linked to a track; the session's facilitator is told",
            "category": "session"
          },
          {
            "slug": "invitation_sent",
            "description": "A workspace or space invitation was sent to someone who already has a user row; they are told",
            "category": "admin"
          },
          {
            "slug": "invitation_accepted",
            "description": "A workspace or space invitation was accepted; the inviter is told",
            "category": "admin"
          },
          {
            "slug": "resource_claim_declared",
            "description": "A capacity or funding claim was declared against a plan; the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "resource_claim_withdrawn",
            "description": "A resource claim was withdrawn; the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "plan_item_placed",
            "description": "A commitment was placed into a plan period; the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "plan_item_removed",
            "description": "A commitment's period placement was withdrawn; the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "decision_recorded",
            "description": "A manual decision was recorded; the deciding person is told",
            "category": "general"
          },
          {
            "slug": "commitment_created",
            "description": "A commitment was created naming an owner; the owner is told",
            "category": "general"
          },
          {
            "slug": "commitment_status_changed",
            "description": "A commitment's status changed; the owner is told",
            "category": "general"
          },
          {
            "slug": "signal_event_classified",
            "description": "A signal's routing class was set or changed; the reporter and the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "signal_event_confirmed",
            "description": "A signal was confirmed; the reporter and the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "signal_event_dismissed",
            "description": "A signal was dismissed; the reporter and the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "signal_verdict_recorded",
            "description": "A reflection-time verdict (held/broke/unobservable) was recorded on a signal; the reporter and the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "signal_event_reopened",
            "description": "A resolved signal was reopened; the reporter and the plan's governance owners are told",
            "category": "cycle"
          },
          {
            "slug": "agent_finding_accepted",
            "description": "An intelligence agent's finding was accepted; workspace admins are told (dismissal is audit-only, not notified)",
            "category": "item"
          },
          {
            "slug": "twins_cluster_item_closed",
            "description": "A Twins cluster resolution closed an item as a duplicate or reframe; the closed item's owner is told",
            "category": "item"
          },
          {
            "slug": "twins_cluster_accepted",
            "description": "A Twins duplicate/variant proposal was accepted and its items became an unresolved cluster (migration 765); the member items' owners are told",
            "category": "item"
          },
          {
            "slug": "session_created",
            "description": "A session was set up with someone else as its facilitator; the facilitator is told",
            "category": "session"
          },
          {
            "slug": "session_invited",
            "description": "Someone was named a participant of a session (participant_ids at create, or a session_participants row); they are told",
            "category": "session"
          },
          {
            "slug": "session_rank_overridden",
            "description": "A decision-authority holder overrode a rank in a run session (override_session_rank, migration 768 trigger); the session's facilitator, creator and participants are told, with the reason",
            "category": "session"
          },
          {
            "slug": "group_scores_withdrawn",
            "description": "The facilitator or a workspace admin withdrew someone's group-session scores before reveal (withdraw_group_scores, migration 768 trigger); the scorer is told",
            "category": "session"
          },
          {
            "slug": "plan_version_created",
            "description": "A new plan version was created; the plan's governance owners (admins + approvers) and the plan cycle's creator are told",
            "category": "cycle"
          },
          {
            "slug": "org_structure_edited",
            "description": "An org unit was renamed, moved, retired or restored; workspace admins are told (one notice per actor per 15-minute burst)",
            "category": "admin"
          },
          {
            "slug": "catchball_revision_requested",
            "description": "Executive requested revision; Middle notified",
            "category": "catchball",
            "proposed": true
          },
          {
            "slug": "catchball_loop_accepted",
            "description": "Executive accepted; all parties notified",
            "category": "catchball",
            "proposed": true
          },
          {
            "slug": "catchball_loop_rejected",
            "description": "Loop rejected; initiator + Middle notified",
            "category": "catchball",
            "proposed": true
          },
          {
            "slug": "catchball_loop_blocked",
            "description": "Loop is blocked; admins + owner notified",
            "category": "catchball",
            "proposed": true
          },
          {
            "slug": "session_invitation",
            "description": "participant invited to session",
            "category": "session",
            "proposed": true
          },
          {
            "slug": "session_attention_needed",
            "description": "facilitator action required",
            "category": "session",
            "proposed": true
          },
          {
            "slug": "item_ownership_transferred",
            "description": "item ownership changed; new owner notified + task created",
            "category": "item",
            "proposed": true
          },
          {
            "slug": "item_readiness_blocker_resolved",
            "description": "a blocker was cleared; waiting parties notified",
            "category": "item",
            "proposed": true
          },
          {
            "slug": "track_owner_assigned",
            "description": "A track was created with an owner, or its owner changed (migration 762 trigger); the new owner is told",
            "category": "admin"
          },
          {
            "slug": "watched_item_changed",
            "description": "An item the person follows changed (migration 864 trigger on audit_events; follows read from watches since 1058); every watcher but the person who changed it is told",
            "category": "item"
          },
          {
            "slug": "followed_record_changed",
            "description": "A cycle, track, session, decision or commitment the person follows changed (migration 1058 trigger on governance_events); every follower but the person who acted is told, once a day per record",
            "category": "general"
          },
          {
            "slug": "facilitator_needed",
            "description": "session needs facilitator; candidates notified",
            "category": "admin",
            "proposed": true
          },
          {
            "slug": "permission_changed",
            "description": "role/permission change; affected user notified",
            "category": "admin",
            "proposed": true
          }
        ]
      },
      {
        "slug": "trigger_condition",
        "description": "what causes this event to fire"
      },
      {
        "slug": "recipients",
        "description": "who receives this notification"
      },
      {
        "slug": "payload",
        "description": "what data is included"
      },
      {
        "slug": "urgency",
        "description": "info | action_required | urgent"
      }
    ],
    "parent": null,
    "children": [],
    "tags": [
      "notification",
      "event",
      "system-communication",
      "audit"
    ],
    "synonyms": [
      "system event",
      "alert type",
      "notification trigger"
    ]
  },
  {
    "family": "Platform & Configuration",
    "name": "Platform Module",
    "slug": "platform-module",
    "type": "taxonomy",
    "status": "draft",
    "tier": 2,
    "domain": "Platform Configuration, Feature Management",
    "description": "A distinct, independently configurable system capability or feature set. Modules can be enabled, partially available, or coming soon. Disabling a module hides the associated UI and deactivates dependent behaviors.\n",
    "purpose": "Allow workspaces to tailor the platform to their organizational model and maturity level. Prevent forcing all workspaces to use all features — especially those intended for large or regulated organizations.\n",
    "dimensions": [
      {
        "slug": "module_id",
        "description": "stable slug identifier"
      },
      {
        "slug": "module_name",
        "description": "human-readable label"
      },
      {
        "slug": "readiness_state",
        "description": "see Module Readiness State register"
      },
      {
        "slug": "enabled",
        "description": "boolean; is it currently on for this workspace"
      },
      {
        "slug": "workspace_scope",
        "description": "which workspaces can enable this"
      },
      {
        "slug": "dependent_modules",
        "description": "modules that must be enabled first"
      },
      {
        "slug": "disabling_effects",
        "description": "what goes away when disabled"
      }
    ],
    "values": [
      {
        "slug": "organizational_hierarchy",
        "description": "Org unit tree, tier derivation, route-to-queue.",
        "readiness_state": "built_and_active",
        "disabling_effects": [
          "hides_org_settings",
          "removes_route_to_queue",
          "simplifies_catchball"
        ]
      },
      {
        "slug": "item_prioritization",
        "description": "Core item ranking, sessions, criteria, results.",
        "readiness_state": "built_and_active",
        "disabling_effects": [
          "removes_sessions",
          "removes_cycle_design"
        ]
      },
      {
        "slug": "session_management",
        "description": "Live and async session execution.",
        "readiness_state": "built_and_active",
        "disabling_effects": [
          "removes_session_creation",
          "removes_live_tools"
        ]
      },
      {
        "slug": "evidence_capture",
        "description": "Audit trail and provenance tracking.",
        "readiness_state": "built_and_active",
        "disabling_effects": [
          "removes_evidence_tab",
          "removes_audit_trail"
        ]
      },
      {
        "slug": "relationships_mapping",
        "description": "Item dependency graph and lineage.",
        "readiness_state": "built_and_active",
        "disabling_effects": [
          "removes_relationship_graph",
          "removes_lineage"
        ]
      },
      {
        "slug": "external_integrations",
        "description": "External tool connectors (Slack, Jira, Miro, Notion, Linear, Zoom).",
        "readiness_state": "built_and_active",
        "note": "Integrations Engine is production-deployed (integrations-engine.vercel.app)"
      },
      {
        "slug": "catchball_governance",
        "description": "Multi-level decision choreography.",
        "readiness_state": "built_incomplete",
        "note": "Core model built; modular toggle not yet wired"
      },
      {
        "slug": "decision_rights_model",
        "description": "Three-axis authority assignments and delegation.",
        "readiness_state": "built_incomplete",
        "note": "Built; modular controls coming"
      },
      {
        "slug": "advanced_analytics",
        "description": "Cycle performance metrics and dashboards.",
        "readiness_state": "coming_soon"
      },
      {
        "slug": "ai_recommendations",
        "description": "LLM-powered tool and priority suggestions.",
        "readiness_state": "coming_soon"
      },
      {
        "slug": "multi_org_governance",
        "description": "Governance across multiple organizations.",
        "readiness_state": "coming_soon"
      }
    ],
    "parent": null,
    "children": [
      "module-readiness-state"
    ],
    "tags": [
      "platform",
      "modularity",
      "feature-management",
      "workspace-config"
    ],
    "synonyms": [
      "feature module",
      "capability module",
      "system module"
    ]
  },
  {
    "family": "Platform & Configuration",
    "name": "Module Readiness State",
    "slug": "module-readiness-state",
    "type": "flat_list",
    "status": "draft",
    "tier": 3,
    "domain": "Platform Configuration",
    "description": "The maturity or implementation completeness of a Platform Module. Communicates to admins whether a module is fully operational, partly built, or planned.\n",
    "purpose": "Set accurate expectations. Prevent admins from expecting features that aren't yet wired. Distinguish \"you have this capability\" from \"this capability is coming.\"\n",
    "dimensions": [
      {
        "slug": "state",
        "description": "see values"
      },
      {
        "slug": "toggle_interaction",
        "description": "selectable | grayed_out | hidden"
      },
      {
        "slug": "ui_indicator",
        "description": "what the admin UI shows for each state"
      }
    ],
    "values": [
      {
        "slug": "active",
        "description": "Fully built, wired, and toggleable",
        "toggle": "enabled",
        "ui": "Green toggle; standard on/off interaction"
      },
      {
        "slug": "built_incomplete",
        "description": "Feature exists in the app but modular controls not yet wired",
        "toggle": "on_but_grayed",
        "ui": "Gray toggle (on) + green 'Enabled' pill to the left"
      },
      {
        "slug": "coming_soon",
        "description": "Planned and prioritized; not yet built",
        "toggle": "grayed_off",
        "ui": "Gray toggle (off) + 'Toggle coming soon' label; non-selectable"
      },
      {
        "slug": "planned",
        "description": "On roadmap; not yet designed",
        "toggle": "hidden_or_minimal",
        "ui": "Smaller display; non-interactive; roadmap note"
      }
    ],
    "parent": "platform-module",
    "children": [],
    "tags": [
      "platform",
      "feature-state",
      "readiness"
    ],
    "synonyms": [
      "module status",
      "feature maturity",
      "module completeness"
    ]
  },
  {
    "family": "Platform & Configuration",
    "name": "Feature Flag",
    "slug": "feature-flag",
    "type": "toggle",
    "status": "draft",
    "tier": 2,
    "domain": "Platform Configuration, Experimentation",
    "description": "A lower-level, granular runtime switch that enables or disables an experimental, phased, or optional feature within the platform. Distinct from Platform Modules (which are stable feature sets): flags are typically temporary, targeted, and may be removed once a feature ships.\n",
    "purpose": "Enable safe experimentation and phased rollout without code deployment. Let teams run targeted experiments with specific users or workspaces before general availability.\n",
    "dimensions": [
      {
        "slug": "flag_id",
        "description": "stable slug identifier"
      },
      {
        "slug": "flag_name",
        "description": "human-readable label"
      },
      {
        "slug": "wired",
        "description": "wiring state of the flag",
        "values": [
          {
            "slug": "wired_and_selectable",
            "description": "Connected to behavior; admin-toggleable"
          },
          {
            "slug": "defined_not_wired",
            "description": "Defined but not connected; non-selectable, shown smaller in UI"
          }
        ]
      },
      {
        "slug": "enabled",
        "description": "boolean; is it currently on for this workspace"
      },
      {
        "slug": "scope",
        "description": "workspace | user | percentage_rollout"
      },
      {
        "slug": "expires_at",
        "description": "when this flag should be reviewed/removed"
      }
    ],
    "parent": null,
    "children": [],
    "tags": [
      "experiment",
      "rollout",
      "optional-feature",
      "runtime-switch"
    ],
    "synonyms": [
      "feature switch",
      "experiment flag",
      "beta feature"
    ]
  },
  {
    "family": "Platform & Configuration",
    "name": "Configuration Metadata Enforcement",
    "slug": "configuration-metadata-enforcement",
    "type": "hybrid",
    "status": "draft",
    "tier": 1,
    "domain": "Platform Behavior, Workspace Customization",
    "description": "The active use of configuration metadata (object types, output shapes, catchball applicability, tool constraints) to govern system behavior — rather than treating configuration as a passive routing hint. Configuration drives what tools are available, what UI is shown, and what actions are valid.\n",
    "purpose": "Prevent the platform from offering tool or workflow choices that are incompatible with the workspace's configuration. Make configuration a first-class behavioral input, not just a label.\n",
    "dimensions": [
      {
        "slug": "config_property",
        "description": "which config property is being enforced"
      },
      {
        "slug": "enforcement_type",
        "description": "see values",
        "values": [
          {
            "slug": "restricts",
            "description": "prevents certain tools or actions"
          },
          {
            "slug": "recommends",
            "description": "changes tool recommendations"
          },
          {
            "slug": "routes",
            "description": "changes navigation / post-selection flow"
          },
          {
            "slug": "populates",
            "description": "auto-fills fields based on config"
          }
        ]
      },
      {
        "slug": "affected_behaviors",
        "description": "what system behaviors change based on this property"
      },
      {
        "slug": "source_config",
        "description": "which configuration object this property lives in"
      }
    ],
    "value_groups": {
      "config_property_examples": [
        {
          "slug": "object_types",
          "description": "what types of items can be in a cycle"
        },
        {
          "slug": "output_shapes",
          "description": "what form the results should take"
        },
        {
          "slug": "catchball_applicable",
          "description": "whether Catchball applies to this decision class"
        },
        {
          "slug": "workflow_footprint",
          "description": "minimal | full (complexity of pre-built config)"
        }
      ]
    },
    "parent": null,
    "children": [
      "configuration-next-step-routing",
      "configuration-workflow-footprint"
    ],
    "tags": [
      "configuration",
      "behavior",
      "governance",
      "workspace-customization"
    ],
    "synonyms": [
      "config-driven behavior",
      "configuration enforcement",
      "metadata governance"
    ]
  },
  {
    "family": "Platform & Configuration",
    "name": "Configuration Next-Step Routing",
    "slug": "configuration-next-step-routing",
    "type": "flat_list",
    "status": "draft",
    "tier": 3,
    "domain": "Platform Behavior, Navigation",
    "description": "The logic that directs a user to the correct page or action after they select a pre-built configuration, based on the configuration's workflow footprint. Different configurations warrant different post-selection flows.\n",
    "purpose": "Prevent users from being dropped into a generic configuration page that doesn't match their intent. Route them directly to where they can start working given what they just configured.\n",
    "dimensions": [
      {
        "slug": "config_type",
        "description": "which configuration was selected"
      },
      {
        "slug": "workflow_footprint",
        "description": "minimal | full (see Configuration Workflow Footprint)"
      },
      {
        "slug": "route",
        "description": "where to navigate after selection"
      }
    ],
    "values": [
      {
        "slug": "drop_in_config",
        "route": "/prioritization/items",
        "reason": "User wants to start working with items immediately; no session needed"
      },
      {
        "slug": "group_plus_session",
        "route": "auto_open_session_launcher",
        "reason": "User needs to set up a session; launcher opens automatically"
      },
      {
        "slug": "catchball_config",
        "route": "/catchball/compose/:cycleId",
        "reason": "User needs to compose a Request Ball; route to composer"
      },
      {
        "slug": "planning_config",
        "route": "/planning/cycle/:cycleId",
        "reason": "User is setting up a plan; route to planning view"
      }
    ],
    "parent": "configuration-metadata-enforcement",
    "children": [],
    "tags": [
      "navigation",
      "configuration",
      "ux-routing"
    ],
    "synonyms": [
      "post-config routing",
      "config landing logic"
    ]
  },
  {
    "family": "Platform & Configuration",
    "name": "Configuration Workflow Footprint",
    "slug": "configuration-workflow-footprint",
    "type": "flat_list",
    "status": "draft",
    "tier": 3,
    "domain": "Platform Behavior, Workspace Customization",
    "description": "A metadata property on a pre-built configuration that describes the complexity and scope of the workflow it enables. Used by Configuration Next-Step Routing to direct post-selection flow.\n",
    "purpose": "Let the system understand \"how much workflow does this configuration set up?\" without needing to inspect all configuration details. A proxy for workflow complexity.\n",
    "dimensions": [
      {
        "slug": "footprint",
        "description": "see values"
      },
      {
        "slug": "description",
        "description": "what this footprint implies for workflow"
      },
      {
        "slug": "typical_use_case",
        "description": "when you'd use this footprint"
      }
    ],
    "values": [
      {
        "slug": "minimal",
        "description": "Sets up just enough to start working; no sessions, no governance",
        "typical_use_case": "Quick prioritization; individual or small team; drop-in",
        "route_hint": "items_list"
      },
      {
        "slug": "standard",
        "description": "Sets up a typical prioritization cycle with sessions",
        "typical_use_case": "Team-level quarterly planning; one or two sessions",
        "route_hint": "session_launcher"
      },
      {
        "slug": "full",
        "description": "Sets up governance, multi-level flow, multiple tracks, sessions",
        "typical_use_case": "Enterprise planning; Catchball enabled; multi-track",
        "route_hint": "cycle_overview"
      }
    ],
    "parent": "configuration-metadata-enforcement",
    "children": [],
    "tags": [
      "configuration",
      "workflow",
      "complexity-signal"
    ],
    "synonyms": [
      "config complexity",
      "workflow scope"
    ]
  },
  {
    "family": "Platform & Configuration",
    "name": "Session Policy Configuration",
    "slug": "session-policy-configuration",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Session Governance, Workspace Customization",
    "description": "Configurable policies that govern session behavior, particularly for edge cases and contested scenarios. Policies can be set at the workspace, cycle, track, or session level, with lower levels able to override higher levels within limits.\n",
    "purpose": "Replace hard-coded session behavior with deliberate organizational choices. Give workspace admins and session owners the ability to decide \"what should happen in this scenario?\" rather than accepting a system default.\n",
    "dimensions": [
      {
        "slug": "policy_type",
        "description": "see values"
      },
      {
        "slug": "policy_scope",
        "description": "workspace | cycle | track | session"
      },
      {
        "slug": "policy_value",
        "description": "the chosen behavior"
      },
      {
        "slug": "override_allowed",
        "description": "can a lower scope override this?"
      }
    ],
    "value_groups": {
      "mid_session_item_addition": {
        "options": [
          "allowed",
          "discouraged",
          "forbidden"
        ],
        "default": "discouraged",
        "note": "Especially sensitive in live sessions with active conversation"
      },
      "participant_addition_mid_session": {
        "options": [
          "allowed",
          "allowed_until_scoring_begins",
          "forbidden"
        ],
        "default": "allowed_until_scoring_begins"
      },
      "score_divergence_resolution": {
        "options": [
          "discussion_optional",
          "discussion_required",
          "facilitator_decides"
        ],
        "default": "discussion_optional"
      },
      "item_pool_lock_timing": {
        "options": [
          "before_invitations",
          "before_scoring",
          "never_locked"
        ],
        "default": "before_scoring"
      },
      "result_submission_requirement": {
        "options": [
          "all_evaluators_must_submit",
          "majority_submits",
          "facilitator_closes"
        ],
        "default": "facilitator_closes"
      }
    },
    "parent": null,
    "children": [],
    "tags": [
      "session",
      "policy",
      "governance",
      "edge-case",
      "workspace-configuration"
    ],
    "synonyms": [
      "session rule",
      "session governance",
      "behavior policy"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "Landscape Metaphor",
    "slug": "landscape-metaphor",
    "type": "hybrid",
    "status": "draft",
    "tier": 1,
    "domain": "Conceptual Framework, Visual Language",
    "description": "The overarching conceptual frame that maps the prioritization process to a mountaineering / wayfinding journey through terrain. Users are travelers moving toward a destination, using instruments, encountering hazards, and navigating via landmarks and paths.\n",
    "purpose": "Make the prioritization model experientially intuitive — not just cognitively describable. Give users an immediate sense of where they are, what's blocking them, and what to do next. Distinguish this system from step-based workflows by surfacing conditions of navigation, not just steps.\n",
    "dimensions": [
      {
        "slug": "terrain_element",
        "description": "which metaphor element (see values)"
      },
      {
        "slug": "mapped_concept",
        "description": "what prioritization concept it represents"
      },
      {
        "slug": "visual_treatment",
        "description": "how it appears in the UI"
      }
    ],
    "values": [
      {
        "slug": "situation",
        "mapped_concept": "Entry point / current problem state",
        "example": "Too many competing priorities"
      },
      {
        "slug": "landmark",
        "mapped_concept": "Key clarity / orientation point",
        "example": "E3 — Outcome Clarity"
      },
      {
        "slug": "path",
        "mapped_concept": "Movement / decision progression",
        "example": "Yes → next landmark"
      },
      {
        "slug": "destination",
        "mapped_concept": "Achieved outcome / end state",
        "example": "Reliable, aligned prioritization"
      },
      {
        "slug": "hazard",
        "mapped_concept": "Predictable obstacle / ambiguity type",
        "example": "Value Ambiguity = no compass"
      },
      {
        "slug": "instrument",
        "mapped_concept": "Tool / intervention to resolve ambiguity",
        "example": "Gap Finder resolves Gap Ambiguity"
      }
    ],
    "parent": null,
    "children": [
      "visual-primitive",
      "hazard-wayfinding",
      "instrument-wayfinding",
      "landmark-wayfinding",
      "path-wayfinding",
      "destination-wayfinding",
      "state-clarity-wayfinding",
      "visual-system-spec"
    ],
    "tags": [
      "metaphor",
      "visual-language",
      "navigation",
      "wayfinding",
      "experiential-design"
    ],
    "synonyms": [
      "prioritization terrain",
      "wayfinding metaphor",
      "terrain metaphor",
      "navigation frame"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "Visual Primitive",
    "slug": "visual-primitive",
    "type": "taxonomy",
    "status": "draft",
    "tier": 2,
    "domain": "Visual Language, Design System",
    "description": "An abstract visual element type in the terrain/wayfinding system. Each primitive has a single primary job, a canonical shape, a semantic color, and an icon family. Together, the six primitives form the complete visual grammar of the terrain map.\n",
    "purpose": "Give designers and engineers a stable, named vocabulary for the visual language. One primitive, one shape. Prevent ad hoc visual choices that erode semantic consistency.\n",
    "dimensions": [
      {
        "slug": "primitive_type",
        "description": "see values"
      },
      {
        "slug": "canonical_shape",
        "description": "the shape that represents this primitive"
      },
      {
        "slug": "color_family",
        "description": "the color family assigned to this primitive"
      },
      {
        "slug": "icon_set",
        "description": "icon family for this primitive"
      },
      {
        "slug": "primary_job",
        "description": "what this primitive communicates at a glance"
      }
    ],
    "values": [
      {
        "slug": "situation",
        "shape": "Rounded rectangle with left notch",
        "color": "Slate / cool gray-blue",
        "primary_job": "Where you are now (entry state)"
      },
      {
        "slug": "landmark",
        "shape": "Hexagon",
        "color": "Deep blue",
        "primary_job": "Where clarity is required (orientation point)"
      },
      {
        "slug": "hazard",
        "shape": "Triangle",
        "color": "Amber → red",
        "primary_job": "What is blocking progress (obstruction)"
      },
      {
        "slug": "instrument",
        "shape": "Circle",
        "color": "Teal",
        "primary_job": "What helps you proceed (tool)"
      },
      {
        "slug": "path",
        "shape": "Line / route segment",
        "color": "Charcoal (default), blue (active), amber (alternate), violet-gray (unknown)",
        "primary_job": "How movement happens (direction)"
      },
      {
        "slug": "destination",
        "shape": "Summit / tapered pentagon / shield-top",
        "color": "Deep green",
        "primary_job": "What better looks like (end state)"
      }
    ],
    "parent": "landscape-metaphor",
    "children": [
      "situation-wayfinding",
      "landmark-wayfinding",
      "hazard-wayfinding",
      "instrument-wayfinding",
      "path-wayfinding",
      "destination-wayfinding"
    ],
    "tags": [
      "visual-language",
      "design-system",
      "semantic-encoding",
      "wayfinding"
    ],
    "synonyms": [
      "map element",
      "terrain component",
      "visual grammar element"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "Hazard (Wayfinding)",
    "slug": "hazard-wayfinding",
    "type": "taxonomy",
    "status": "draft",
    "tier": 1,
    "domain": "Wayfinding, Ambiguity Diagnosis",
    "description": "A named, predictable obstacle or ambiguity type that impedes progress in the prioritization journey. Each hazard has a terrain metaphor equivalent, a canonical icon, and a paired Instrument to resolve it.\n",
    "purpose": "Make the landscape of \"what can go wrong\" visible, named, and navigable. A user who sees a Hazard should immediately know: what kind of problem this is, what's being obscured, and what tool to use.\n",
    "dimensions": [
      {
        "slug": "hazard_type",
        "description": "see values"
      },
      {
        "slug": "landscape_equivalent",
        "description": "the terrain metaphor for this ambiguity"
      },
      {
        "slug": "paired_instrument",
        "description": "which Instrument resolves this Hazard"
      },
      {
        "slug": "icon",
        "description": "canonical icon for this hazard type"
      },
      {
        "slug": "severity_level",
        "description": "caution | obstruction | passive warning"
      }
    ],
    "values": [
      {
        "slug": "outcome_ambiguity",
        "landscape": "No visible summit",
        "paired_instrument": "outcome_lens",
        "icon": "Faded target",
        "effect": "Cannot define success; movement is directionless"
      },
      {
        "slug": "reality_ambiguity",
        "landscape": "Fog / low visibility",
        "paired_instrument": "gap_finder",
        "icon": "Obscured eye / fog cloud",
        "effect": "Cannot see current state clearly; comparison impossible"
      },
      {
        "slug": "gap_ambiguity",
        "landscape": "No clear route between points",
        "paired_instrument": "gap_finder",
        "icon": "Broken bridge",
        "effect": "Cannot identify what is missing between current and desired"
      },
      {
        "slug": "scope_ambiguity",
        "landscape": "Overgrown terrain / too many paths",
        "paired_instrument": "structure_builder",
        "icon": "Tangled path / overgrown routes",
        "effect": "Cannot determine what is in or out"
      },
      {
        "slug": "structural_ambiguity",
        "landscape": "Broken or missing map",
        "paired_instrument": "structure_builder",
        "icon": "Disconnected nodes / crumpled map",
        "effect": "Cannot identify relationships or dependencies"
      },
      {
        "slug": "value_ambiguity",
        "landscape": "No compass",
        "paired_instrument": "value_compass",
        "icon": "Compass without needle / spinning compass",
        "effect": "Movement is random; debates go in circles"
      },
      {
        "slug": "decision_ambiguity",
        "landscape": "Fork with no signposts",
        "paired_instrument": "value_compass",
        "icon": "Blank signpost / fork with no labels",
        "effect": "Cannot determine which option to choose"
      },
      {
        "slug": "alignment_ambiguity",
        "landscape": "Group splitting apart",
        "paired_instrument": "relationship_mapper",
        "icon": "Diverging arrows / split formation",
        "effect": "Team is not moving in the same direction"
      },
      {
        "slug": "execution_ambiguity",
        "landscape": "Path disappears",
        "paired_instrument": "criteria_builder",
        "icon": "Fading path / path that ends abruptly",
        "effect": "Plan is unclear; cannot begin execution"
      },
      {
        "slug": "adaptation_ambiguity",
        "landscape": "Weather shifts with no warning",
        "paired_instrument": "outcome_lens",
        "icon": "Storm over route / weather vane",
        "effect": "Conditions have changed; previous plan may be invalid"
      }
    ],
    "parent": "landscape-metaphor",
    "children": [],
    "tags": [
      "wayfinding",
      "ambiguity",
      "obstacle",
      "diagnosis",
      "visual-language"
    ],
    "synonyms": [
      "ambiguity type",
      "navigation obstacle",
      "clarity failure"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "Instrument (Wayfinding)",
    "slug": "instrument-wayfinding",
    "type": "taxonomy",
    "status": "draft",
    "tier": 2,
    "domain": "Wayfinding, Tools & Interventions",
    "description": "A named tool or intervention that helps resolve a specific Hazard and enable forward movement in the prioritization journey. Each Instrument is paired with one or more Hazard types and produces a specific output.\n",
    "purpose": "Provide concrete, contextual aid to users who are stuck. When a Hazard is identified, the paired Instrument should appear automatically and tell the user exactly what to do.\n",
    "dimensions": [
      {
        "slug": "instrument_type",
        "description": "see values"
      },
      {
        "slug": "resolves_hazards",
        "description": "which Hazard types this instrument addresses"
      },
      {
        "slug": "primary_landmark",
        "description": "which Landmark this instrument is associated with"
      },
      {
        "slug": "produces_output",
        "description": "what the user creates by using this instrument"
      },
      {
        "slug": "icon",
        "description": "canonical icon"
      }
    ],
    "values": [
      {
        "slug": "outcome_lens",
        "resolves_hazards": [
          "outcome_ambiguity",
          "adaptation_ambiguity"
        ],
        "primary_landmark": "outcome_landmark",
        "produces_output": "Outcome statement (what success looks like)",
        "icon": "Lens over target"
      },
      {
        "slug": "gap_finder",
        "resolves_hazards": [
          "gap_ambiguity",
          "reality_ambiguity"
        ],
        "primary_landmark": "gap_landmark",
        "produces_output": "Gap statements (what is missing between current and desired)",
        "icon": "Magnifier over split terrain"
      },
      {
        "slug": "value_compass",
        "resolves_hazards": [
          "value_ambiguity",
          "decision_ambiguity"
        ],
        "primary_landmark": "value_landmark",
        "produces_output": "Criteria statements (what we value and how much)",
        "icon": "Compass with highlighted bearing"
      },
      {
        "slug": "structure_builder",
        "resolves_hazards": [
          "structural_ambiguity",
          "scope_ambiguity"
        ],
        "primary_landmark": "structure_landmark",
        "produces_output": "Structure definition (attributes, categories, taxonomy)",
        "icon": "Grid + tool"
      },
      {
        "slug": "relationship_mapper",
        "resolves_hazards": [
          "alignment_ambiguity"
        ],
        "primary_landmark": "alignment_landmark",
        "produces_output": "Dependency/relationship map",
        "icon": "Connected nodes"
      },
      {
        "slug": "criteria_builder",
        "resolves_hazards": [
          "execution_ambiguity"
        ],
        "primary_landmark": "execution_landmark",
        "produces_output": "Evaluation criteria set",
        "icon": "Sliders / weighted bars"
      }
    ],
    "parent": "landscape-metaphor",
    "children": [],
    "tags": [
      "wayfinding",
      "tool",
      "intervention",
      "clarity-resolver",
      "visual-language"
    ],
    "synonyms": [
      "wayfinding tool",
      "clarity instrument",
      "navigation aid"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "Landmark (Wayfinding)",
    "slug": "landmark-wayfinding",
    "type": "taxonomy",
    "status": "draft",
    "tier": 2,
    "domain": "Wayfinding, Clarity Points",
    "description": "A named, critical point in the prioritization journey where clarity or orientation is required before proceeding. A Landmark is not just a step — it is an obligation point. You must stop here and orient.\n",
    "purpose": "Signal which moments in the journey carry the most cognitive weight. Prevent users from proceeding without the clarity that a given landmark demands. Make orientation visible and non-optional.\n",
    "dimensions": [
      {
        "slug": "landmark_type",
        "description": "see values"
      },
      {
        "slug": "maps_to_node",
        "description": "which E-node in the model (E3, E5, E8, etc.)"
      },
      {
        "slug": "required_clarity",
        "description": "what must be true before leaving this landmark"
      },
      {
        "slug": "associated_hazards",
        "description": "which Hazards are most likely here"
      },
      {
        "slug": "associated_instrument",
        "description": "which Instrument is offered here"
      },
      {
        "slug": "icon",
        "description": "canonical icon"
      }
    ],
    "values": [
      {
        "slug": "outcome_landmark",
        "maps_to": "E3",
        "required_clarity": "We can describe what success looks like",
        "associated_hazards": [
          "outcome_ambiguity"
        ],
        "icon": "Target / summit flag"
      },
      {
        "slug": "reality_landmark",
        "maps_to": "E4",
        "required_clarity": "We have a clear picture of current state",
        "associated_hazards": [
          "reality_ambiguity"
        ],
        "icon": "Eye / field view / map grid"
      },
      {
        "slug": "gap_landmark",
        "maps_to": "E5",
        "required_clarity": "We can describe the gap between current and desired",
        "associated_hazards": [
          "gap_ambiguity",
          "reality_ambiguity"
        ],
        "icon": "Broken bridge / canyon split"
      },
      {
        "slug": "value_landmark",
        "maps_to": "E8",
        "required_clarity": "We know what we value and in what order",
        "associated_hazards": [
          "value_ambiguity",
          "decision_ambiguity"
        ],
        "icon": "Compass"
      },
      {
        "slug": "structure_landmark",
        "maps_to": "E6",
        "required_clarity": "Items have consistent attributes and categories",
        "associated_hazards": [
          "structural_ambiguity",
          "scope_ambiguity"
        ],
        "icon": "Grid / scaffold"
      },
      {
        "slug": "alignment_landmark",
        "maps_to": "E10",
        "required_clarity": "Stakeholders agree on direction",
        "associated_hazards": [
          "alignment_ambiguity"
        ],
        "icon": "Linked nodes / braided lines"
      },
      {
        "slug": "execution_landmark",
        "maps_to": "E11",
        "required_clarity": "We know what to build, by whom, and when",
        "associated_hazards": [
          "execution_ambiguity"
        ],
        "icon": "Trail marker / road"
      }
    ],
    "parent": "landscape-metaphor",
    "children": [],
    "tags": [
      "wayfinding",
      "clarity-point",
      "orientation",
      "visual-language"
    ],
    "synonyms": [
      "clarity node",
      "orientation point",
      "clarity gate"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "Path (Wayfinding)",
    "slug": "path-wayfinding",
    "type": "graph",
    "status": "draft",
    "tier": 2,
    "domain": "Wayfinding, Movement & Progression",
    "description": "A visual connector between nodes in the terrain map, encoding the type and state of movement. Paths carry semantic meaning: solid blue means forward progress; dashed amber means alternate route; fog means paused movement.\n",
    "purpose": "Encode not just that movement happened, but what kind of movement. The path style should communicate at a glance whether progress is canonical, alternative, or stalled.\n",
    "dimensions": [
      {
        "slug": "path_type",
        "description": "see values"
      },
      {
        "slug": "from_node",
        "description": "origin node"
      },
      {
        "slug": "to_node",
        "description": "destination node"
      },
      {
        "slug": "line_style",
        "description": "solid | dashed | dotted"
      },
      {
        "slug": "color",
        "description": "blue | amber | violet-gray"
      },
      {
        "slug": "label",
        "description": "Yes | No | I don't know"
      }
    ],
    "values": [
      {
        "slug": "yes_path",
        "label": "Yes",
        "line_style": "solid",
        "color": "blue",
        "meaning": "Canonical forward movement; clarity achieved"
      },
      {
        "slug": "no_path",
        "label": "No",
        "line_style": "dashed",
        "color": "amber",
        "meaning": "Alternate route; still legitimate, not canonical"
      },
      {
        "slug": "i_dont_know_path",
        "label": "I don't know",
        "line_style": "foggy connector",
        "color": "soft amber",
        "meaning": "Movement paused pending visibility; not a route forward yet"
      },
      {
        "slug": "loopback_path",
        "label": "Revisit",
        "line_style": "curved dashed",
        "color": "violet-gray",
        "meaning": "Returning to a previous node for reconsideration"
      }
    ],
    "parent": "landscape-metaphor",
    "children": [],
    "tags": [
      "wayfinding",
      "movement",
      "decision-encoding",
      "visual-language"
    ],
    "synonyms": [
      "route",
      "connector",
      "navigation transition"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "Destination (Wayfinding)",
    "slug": "destination-wayfinding",
    "type": "flat_list",
    "status": "draft",
    "tier": 2,
    "domain": "Wayfinding, Outcomes",
    "description": "The visible end state in the prioritization terrain — the summit. Represents what \"better\" or \"aligned execution\" looks like when the journey is complete. Remains visually present throughout the journey as an orienting anchor.\n",
    "purpose": "Give users a persistent visual reminder of what they are moving toward. Prevent losing sight of the goal in the midst of messy prioritization work.\n",
    "dimensions": [
      {
        "slug": "destination_type",
        "description": "terminal | milestone"
      },
      {
        "slug": "outcome_statement",
        "description": "what this destination means in plain language"
      },
      {
        "slug": "visibility_state",
        "description": "visible_unreached | reached | selected",
        "values": [
          {
            "slug": "visible_unreached",
            "description": "in view but not yet achieved"
          },
          {
            "slug": "reached",
            "description": "journey complete; destination achieved"
          },
          {
            "slug": "selected",
            "description": "user is focusing on this destination"
          }
        ]
      }
    ],
    "parent": "landscape-metaphor",
    "children": [],
    "tags": [
      "wayfinding",
      "end-state",
      "outcome",
      "goal"
    ],
    "synonyms": [
      "journey end state",
      "summit",
      "target outcome"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "State Clarity (Wayfinding)",
    "slug": "state-clarity-wayfinding",
    "type": "flat_list",
    "status": "draft",
    "tier": 2,
    "domain": "Wayfinding, Visual State",
    "description": "The visual/semantic state of any node on the terrain map, indicating whether it has been resolved, is active, is unclear, or has not yet been visited. Applied as an overlay on the node's base primitive color.\n",
    "purpose": "Communicate local readiness at every node without replacing the node's semantic identity. A Landmark remains a Landmark (blue hexagon) while also communicating its current state (fog = unclear; green accent = resolved).\n",
    "dimensions": [
      {
        "slug": "state",
        "description": "see values"
      },
      {
        "slug": "visual_encoding",
        "description": "color accent, glow, overlay, badge"
      },
      {
        "slug": "user_interpretation",
        "description": "what this means in plain language"
      }
    ],
    "values": [
      {
        "slug": "not_started",
        "visual": "Neutral gray outline",
        "meaning": "Not yet visited; no response given"
      },
      {
        "slug": "active",
        "visual": "Blue glow / stronger border",
        "meaning": "Currently selected or in progress"
      },
      {
        "slug": "yes_resolved",
        "visual": "Green accent",
        "meaning": "Clarity achieved; forward path taken"
      },
      {
        "slug": "no_declined",
        "visual": "Muted amber-brown accent",
        "meaning": "Alternate route taken; canonical path declined"
      },
      {
        "slug": "i_dont_know",
        "visual": "Amber haze / fog treatment",
        "meaning": "Clarity not yet achieved; instrument needed before moving"
      }
    ],
    "parent": "landscape-metaphor",
    "children": [],
    "tags": [
      "wayfinding",
      "visual-state",
      "node-condition",
      "visual-language"
    ],
    "synonyms": [
      "node state",
      "clarity state",
      "local state"
    ]
  },
  {
    "family": "Visual & Navigation",
    "name": "Visual System Spec",
    "slug": "visual-system-spec",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Design System, Visual Language",
    "description": "The complete specification for implementing the wayfinding/terrain visual language. Defines all shape tokens, color tokens, state tokens, icon families, typography roles, composition rules, and component inventory needed to build the terrain map consistently.\n",
    "purpose": "Translate the Landscape Metaphor from concept to buildable design system. Give designers and engineers everything they need to create consistent, semantically correct terrain maps in any medium (Figma, code, slides).\n",
    "dimensions": [
      {
        "slug": "spec_layer",
        "description": "foundations | primitives | states | components | sample_scenes"
      },
      {
        "slug": "token_type",
        "description": "shape | color | state | icon"
      },
      {
        "slug": "component_name",
        "description": "named UI component"
      },
      {
        "slug": "composition_rule",
        "description": "how elements relate spatially"
      }
    ],
    "value_groups": {
      "shape_tokens": [
        {
          "slug": "shape/situation"
        },
        {
          "slug": "shape/landmark"
        },
        {
          "slug": "shape/hazard"
        },
        {
          "slug": "shape/instrument"
        },
        {
          "slug": "shape/destination"
        },
        {
          "slug": "shape/path/solid"
        },
        {
          "slug": "shape/path/dashed"
        },
        {
          "slug": "shape/path/fog"
        }
      ],
      "color_tokens": [
        {
          "slug": "color/situation"
        },
        {
          "slug": "color/landmark"
        },
        {
          "slug": "color/hazard/low"
        },
        {
          "slug": "color/hazard/high"
        },
        {
          "slug": "color/instrument"
        },
        {
          "slug": "color/destination"
        },
        {
          "slug": "color/path/default"
        },
        {
          "slug": "color/path/yes"
        },
        {
          "slug": "color/path/no"
        },
        {
          "slug": "color/path/unknown"
        }
      ],
      "state_tokens": [
        {
          "slug": "state/selected"
        },
        {
          "slug": "state/resolved"
        },
        {
          "slug": "state/declined"
        },
        {
          "slug": "state/unclear"
        }
      ],
      "component_inventory": {
        "frame": [
          "context_frame",
          "legend_panel"
        ],
        "map": [
          "situation_card",
          "landmark_node",
          "destination_node",
          "path_connector",
          "path_label"
        ],
        "hazard": [
          "hazard_marker",
          "fog_overlay",
          "fracture_block_overlay"
        ],
        "instrument": [
          "instrument_node",
          "instrument_tray"
        ],
        "detail": [
          "detail_panel",
          "state_chip",
          "response_control",
          "prompt_block",
          "exercise_card",
          "output_card"
        ]
      },
      "composition_rules": [
        {
          "slug": "Situations sit at the left/bottom-left edge"
        },
        {
          "slug": "Landmarks sit on or just above the main path"
        },
        {
          "slug": "Hazards sit adjacent to the path, not on top of it"
        },
        {
          "slug": "Instruments appear below or beside the active landmark"
        },
        {
          "slug": "Destinations remain visually elevated (top-right or horizon)"
        }
      ]
    },
    "parent": "landscape-metaphor",
    "children": [],
    "tags": [
      "design-system",
      "visual-language",
      "implementation-spec",
      "tokens"
    ],
    "synonyms": [
      "visual spec",
      "terrain design system",
      "atomic segmentation visual system"
    ]
  },
  {
    "family": "Strategy",
    "name": "Strategy Kernel",
    "slug": "strategy-kernel",
    "type": "hierarchy",
    "status": "draft",
    "tier": 2,
    "domain": "Strategy, Decision Foundation",
    "description": "The core logical structure of a strategy: a diagnosis of the situation, a guiding policy that defines the approach, and the coherent set of actions that follow from it. Based on Richard Rumelt's \"Good Strategy / Bad Strategy\" framework.\n",
    "purpose": "Prevent strategy from being reduced to aspirational goals and financial targets. Force explicit articulation of *why* this approach and *how* the actions cohere around the policy.\n",
    "dimensions": [
      {
        "slug": "diagnosis",
        "description": "Names the critical obstacle or defining condition",
        "type": "string"
      },
      {
        "slug": "guiding_policy",
        "description": "The approach or principle that channels effort",
        "type": "string"
      },
      {
        "slug": "coherent_actions",
        "description": "Actions that reinforce each other and follow from the policy",
        "type": "list[string]"
      }
    ],
    "parent": null,
    "children": [
      "strategy-assumptions",
      "proximate-objectives"
    ],
    "tags": [
      "strategy",
      "diagnosis",
      "policy",
      "coherence"
    ],
    "synonyms": [
      "strategic kernel",
      "core strategy",
      "strategy logic"
    ]
  },
  {
    "family": "Strategy",
    "name": "Strategy Assumptions",
    "slug": "strategy-assumptions",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Strategy, Risk & Validation",
    "description": "The explicit hypotheses that underpin the Strategy Kernel — the things that must be true for the strategy to work. Named so they can be monitored, tested, and updated as conditions change.\n",
    "purpose": "Make strategy falsifiable. A strategy built on unnamed assumptions cannot be tested or improved. By naming them, teams can design validation activities and watch for disconfirming signals.\n",
    "dimensions": [
      {
        "slug": "assumption_id",
        "description": "unique identifier"
      },
      {
        "slug": "assumption_text",
        "description": "what must be true"
      },
      {
        "slug": "assumption_type",
        "description": "market | technical | organizational | competitive",
        "values": [
          {
            "slug": "market",
            "description": "about customer behavior or market dynamics"
          },
          {
            "slug": "technical",
            "description": "about technical feasibility"
          },
          {
            "slug": "organizational",
            "description": "about team capability or capacity"
          },
          {
            "slug": "competitive",
            "description": "about competitor behavior"
          },
          {
            "slug": "regulatory",
            "description": "about external rules or constraints"
          }
        ]
      },
      {
        "slug": "confidence_level",
        "description": "speculative | informed | validated | proven",
        "values": [
          {
            "slug": "speculative",
            "description": "gut feeling; no data"
          },
          {
            "slug": "informed",
            "description": "experience or indirect evidence"
          },
          {
            "slug": "validated",
            "description": "direct evidence; tested and confirmed"
          },
          {
            "slug": "proven",
            "description": "consistently demonstrated; very high confidence"
          }
        ]
      },
      {
        "slug": "validation_method",
        "description": "how this will be tested (if not already validated)"
      },
      {
        "slug": "validation_status",
        "description": "untested | in_progress | validated | invalidated"
      },
      {
        "slug": "associated_risks",
        "description": "what happens if this assumption is wrong"
      }
    ],
    "parent": "strategy-kernel",
    "children": [],
    "tags": [
      "strategy",
      "assumptions",
      "validation",
      "risk",
      "hypothesis"
    ],
    "synonyms": [
      "strategic hypotheses",
      "strategy risks",
      "underlying assumptions"
    ]
  },
  {
    "family": "Strategy",
    "name": "Proximate Objectives",
    "slug": "proximate-objectives",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Strategy, Execution Planning",
    "description": "Feasible, near-term targets that operationalize the Strategy Kernel into work that can be planned and executed within one or two cycles. More concrete than strategic goals; less detailed than item specifications.\n",
    "purpose": "Bridge the gap between high-level strategy (Kernel) and specific work items (Items in DEFINING). Make strategy actionable at the cycle level without requiring every item to connect directly to the Kernel.\n",
    "dimensions": [
      {
        "slug": "objective_id",
        "description": "unique identifier"
      },
      {
        "slug": "objective_text",
        "description": "what we're trying to achieve in this cycle"
      },
      {
        "slug": "associated_kernel",
        "description": "which Strategy Kernel this operationalizes"
      },
      {
        "slug": "cycle_scope",
        "description": "which cycle(s) this objective spans"
      },
      {
        "slug": "success_metric",
        "description": "how we'll know this objective is achieved"
      },
      {
        "slug": "contributing_items",
        "description": "items in the item pool that contribute to this"
      },
      {
        "slug": "confidence_level",
        "description": "how confident we are this is the right objective"
      }
    ],
    "parent": "strategy-kernel",
    "children": [],
    "tags": [
      "strategy",
      "objective",
      "near-term",
      "cycle-planning",
      "operationalization"
    ],
    "synonyms": [
      "strategic objective",
      "near-term target",
      "cycle objective"
    ]
  },
  {
    "family": "Strategy",
    "name": "Plan of Record",
    "slug": "plan-of-record",
    "type": "hybrid",
    "status": "draft",
    "tier": 2,
    "domain": "Strategy, Execution Authorization",
    "description": "An authorized, point-in-time snapshot of committed execution: the work that has been ratified to proceed, the capacity allocated, and the dependencies acknowledged. The Plan of Record is the output of the Planning mode; it represents organizational commitment.\n",
    "purpose": "Create a stable, referenced baseline for execution planning. Distinguish \"what we decided to do\" (Published Priority List, which ranks desirability) from \"what we are actually committed to doing\" (Plan of Record, which assigns capacity and confirms constraints).\n",
    "dimensions": [
      {
        "slug": "por_id",
        "description": "unique identifier"
      },
      {
        "slug": "cycle_id",
        "description": "which cycle this POR covers"
      },
      {
        "slug": "status",
        "description": "draft | ratified | superseded",
        "values": [
          {
            "slug": "draft",
            "description": "being assembled; not yet committed"
          },
          {
            "slug": "ratified",
            "description": "authorized; execution can proceed"
          },
          {
            "slug": "superseded",
            "description": "replaced by a newer POR (replan)"
          }
        ]
      },
      {
        "slug": "committed_items",
        "description": "ordered list of items with capacity assignment"
      },
      {
        "slug": "capacity_allocation",
        "description": "how capacity is distributed across items/tracks"
      },
      {
        "slug": "work_graph",
        "description": "dependencies between committed items"
      },
      {
        "slug": "ratified_by",
        "description": "who authorized this POR"
      },
      {
        "slug": "ratified_at",
        "description": "when it was authorized"
      },
      {
        "slug": "supersedes",
        "description": "which previous POR this replaces (if applicable)"
      }
    ],
    "parent": null,
    "children": [],
    "tags": [
      "strategy",
      "execution",
      "commitment",
      "planning",
      "authorization"
    ],
    "synonyms": [
      "execution plan",
      "committed roadmap",
      "ratified plan"
    ]
  },
  {
    "family": "Gap Registers",
    "name": "State Transition Rule",
    "slug": "state-transition-rule",
    "type": "hybrid",
    "status": "proposed",
    "tier": 2,
    "domain": "Workflow, State Machine Logic",
    "description": "An explicit, named rule that describes a valid transition from one state to another within a given state machine (Item Lifecycle, Catchball Loop State, Session State, etc.). Specifies what triggers the transition, what conditions must be met, and what happens as a result.\n",
    "purpose": "Make workflow logic queryable and auditable. Prevent implicit or hidden state transition logic scattered across code. Enable \"why did this item move from QUALIFYING to DEFINING?\" to be answered by reference to a named rule.\n",
    "dimensions": [
      {
        "slug": "rule_id",
        "description": "unique identifier"
      },
      {
        "slug": "state_machine",
        "description": "which register's states this rule governs"
      },
      {
        "slug": "from_state",
        "description": "source state"
      },
      {
        "slug": "to_state",
        "description": "destination state"
      },
      {
        "slug": "trigger",
        "description": "what causes this transition (user action, event, time)"
      },
      {
        "slug": "preconditions",
        "description": "what must be true before the transition is valid"
      },
      {
        "slug": "postconditions",
        "description": "what becomes true after the transition occurs"
      },
      {
        "slug": "transition_type",
        "description": "manual | automatic | conditional",
        "values": [
          {
            "slug": "manual",
            "example": "Facilitator clicks 'Go Live' → session state SETUP → LIVE"
          },
          {
            "slug": "automatic",
            "example": "All evaluators submit → session state LIVE → COMPLETED"
          },
          {
            "slug": "conditional",
            "example": "All Loop Readiness Gates pass → ball can be advanced (not auto-advanced; gates being met doesn't toss the ball)"
          }
        ]
      },
      {
        "slug": "reversible",
        "description": "can this transition be undone?"
      }
    ],
    "parent": null,
    "children": [],
    "tags": [
      "workflow",
      "state-machine",
      "transition",
      "audit",
      "logic"
    ],
    "synonyms": [
      "workflow rule",
      "transition condition",
      "state change rule"
    ]
  },
  {
    "family": "Gap Registers",
    "name": "User Action Type",
    "slug": "user-action-type",
    "type": "taxonomy",
    "status": "proposed",
    "tier": 2,
    "domain": "Workflow, User Interaction",
    "description": "A standardized vocabulary of actions users can take within the system. Each action type has a name, a domain, a set of preconditions, and a set of state/data effects. Actions are the verbs of the Claritas model.\n",
    "purpose": "Replace ad hoc action naming across the UI and codebase with a canonical, queryable vocabulary. Enable \"what actions can I take right now?\" to be answered deterministically based on the user's role, the current state, and the context.\n",
    "dimensions": [
      {
        "slug": "action_name",
        "description": "canonical verb (see values)"
      },
      {
        "slug": "domain",
        "description": "which feature area this action belongs to"
      },
      {
        "slug": "required_role",
        "description": "which roles can perform this action"
      },
      {
        "slug": "preconditions",
        "description": "what must be true before this action is valid"
      },
      {
        "slug": "effects",
        "description": "what state or data changes result"
      },
      {
        "slug": "reversible",
        "description": "can this action be undone?"
      }
    ],
    "value_groups": {
      "catchball": [
        {
          "slug": "toss_ball",
          "description": "executive initiates request; ball goes to lower tier"
        },
        {
          "slug": "pass_ball",
          "description": "receiver returns proposal; ball goes to upper tier"
        },
        {
          "slug": "involve",
          "description": "receiver involves a sub-tier without transferring ball"
        },
        {
          "slug": "accept_proposal",
          "description": "tossing tier accepts; loop ends"
        },
        {
          "slug": "request_revision",
          "description": "tossing tier returns with feedback"
        },
        {
          "slug": "reject_proposal",
          "description": "tossing tier definitively rejects"
        },
        {
          "slug": "raise_clarification",
          "description": "ask question without moving ball"
        },
        {
          "slug": "raise_blocker",
          "description": "name an obstruction preventing advancement"
        }
      ],
      "item": [
        {
          "slug": "create_item",
          "description": "add new item to workspace"
        },
        {
          "slug": "enrich_item",
          "description": "add detail during ENRICHING stage"
        },
        {
          "slug": "move_to_qualifying",
          "description": "advance item to QUALIFYING"
        },
        {
          "slug": "move_to_defining",
          "description": "advance item to DEFINING"
        },
        {
          "slug": "archive_item",
          "description": "remove from active consideration"
        },
        {
          "slug": "duplicate_item",
          "description": "create copy (may trigger dedup review)"
        },
        {
          "slug": "transfer_ownership",
          "description": "change item owner (triggers notification + task)"
        },
        {
          "slug": "link_related",
          "description": "associate with another item (relationship)"
        }
      ],
      "session": [
        {
          "slug": "create_session",
          "description": "set up new session"
        },
        {
          "slug": "open_for_participants",
          "description": "allow participants to join"
        },
        {
          "slug": "start_session",
          "description": "begin live scoring"
        },
        {
          "slug": "score_item",
          "description": "evaluator assigns score to item"
        },
        {
          "slug": "submit_scores",
          "description": "evaluator confirms their scoring is complete"
        },
        {
          "slug": "close_session",
          "description": "facilitator ends session"
        },
        {
          "slug": "publish_results",
          "description": "submit priority list"
        }
      ],
      "governance": [
        {
          "slug": "assign_decision_rights",
          "description": "grant authority over a decision class"
        },
        {
          "slug": "delegate_authority",
          "description": "transfer authority with constraints"
        },
        {
          "slug": "grant_permission",
          "description": "explicit capability grant to a role"
        },
        {
          "slug": "revoke_permission",
          "description": "explicit capability revocation"
        },
        {
          "slug": "assign_track_owner",
          "description": "set owner for a track (triggers notification)"
        }
      ]
    },
    "parent": null,
    "children": [],
    "tags": [
      "user-action",
      "workflow",
      "vocabulary",
      "permissions"
    ],
    "synonyms": [
      "system action",
      "user operation",
      "action vocabulary"
    ]
  },
  {
    "family": "Gap Registers",
    "name": "Temporal Constraint Type",
    "slug": "temporal-constraint-type",
    "type": "taxonomy",
    "status": "proposed",
    "tier": 3,
    "domain": "Workflow, Scheduling, Time-Based Logic",
    "description": "Named categories of time-based rules, deadlines, or constraints that govern when actions can occur, how long processes have, or what happens when time expires. Makes time a first-class concern in workflow design.\n",
    "purpose": "Prevent workflows from being designed as if time is unlimited or uniform. Give teams a vocabulary for discussing deadline pressure, availability windows, recurrence, and time-based escalation — without conflating these into generic \"due dates.\"\n",
    "dimensions": [
      {
        "slug": "constraint_type",
        "description": "see values"
      },
      {
        "slug": "applies_to",
        "description": "which workflow or object this constraint governs"
      },
      {
        "slug": "value",
        "description": "the time value (date, duration, recurrence pattern)"
      },
      {
        "slug": "enforcement",
        "description": "soft (warning) | hard (block) | auto_escalate"
      },
      {
        "slug": "breach_behavior",
        "description": "what happens when time expires"
      }
    ],
    "values": [
      {
        "slug": "hard_deadline",
        "description": "Must be completed by this date or the action is blocked",
        "enforcement": "hard",
        "example": "Loop Intent due_date: ball must be returned by Friday EOD"
      },
      {
        "slug": "soft_target",
        "description": "Desired completion date; warning if missed; not blocked",
        "enforcement": "soft",
        "example": "Item should be in DEFINING by start of Q2 planning"
      },
      {
        "slug": "availability_window",
        "description": "Action only valid during a time window",
        "enforcement": "hard",
        "example": "Session is live only from 2:00–3:30 PM; cannot score outside window"
      },
      {
        "slug": "timeout",
        "description": "If no action within this duration, auto-trigger a consequence",
        "enforcement": "auto_escalate",
        "example": "If ball sits at Middle for >5 days with no activity, escalate to admin"
      },
      {
        "slug": "recurrence",
        "description": "Process repeats on a schedule",
        "enforcement": "hard",
        "example": "Quarterly planning cycle recurs every 13 weeks"
      },
      {
        "slug": "cooling_off_period",
        "description": "Must wait this long before an action can be repeated",
        "enforcement": "hard",
        "example": "Cannot re-toss ball on same decision class within 2 weeks of final rejection"
      }
    ],
    "parent": null,
    "children": [],
    "tags": [
      "time",
      "deadline",
      "scheduling",
      "constraint",
      "workflow"
    ],
    "synonyms": [
      "time constraint",
      "scheduling rule",
      "deadline type"
    ]
  },
  {
    "family": "Gap Registers",
    "name": "System Failure Type",
    "slug": "system-failure-type",
    "type": "taxonomy",
    "status": "proposed",
    "tier": 3,
    "domain": "Reliability, Error Handling",
    "description": "A named category of system error, failure mode, or anti-pattern that produces incorrect or misleading behavior. Enables systematic diagnosis, prevention, and recovery strategy design.\n",
    "purpose": "Replace ad hoc error handling with named, systematic failure categories. Enable \"what kind of failure is this?\" to have a canonical answer, which in turn guides the correct recovery strategy and prevention design.\n",
    "dimensions": [
      {
        "slug": "failure_type",
        "description": "see values"
      },
      {
        "slug": "detection_signal",
        "description": "how this failure manifests / is detected"
      },
      {
        "slug": "typical_cause",
        "description": "root causes"
      },
      {
        "slug": "recovery_strategy",
        "description": "how to handle when detected"
      },
      {
        "slug": "prevention_pattern",
        "description": "how to design against this failure class"
      }
    ],
    "values": [
      {
        "slug": "silent_db_failure",
        "description": "Database write is assumed to succeed; no confirmation checked; UI shows success incorrectly",
        "detection": "Data appears saved but is gone on refresh; no error in UI",
        "typical_cause": "Optimistic updates without awaiting DB confirmation",
        "recovery": "Await all DB writes; dispatch dispatchStoreError on failure; rollback UI",
        "prevention": "Never show success toast before DB confirmation"
      },
      {
        "slug": "rls_violation",
        "description": "Row-level security policy prevents DB operation; error is returned but not surfaced to user",
        "detection": "403 in network tab; component may silently fail or show stale data",
        "typical_cause": "Incorrect or missing RLS policy; wrong workspace scope on query",
        "recovery": "Surface error to user; log to error tracker; fix RLS policy",
        "prevention": "Test RLS policies for all roles before feature ships"
      },
      {
        "slug": "uninitialized_variable",
        "description": "Code references a variable before it is defined; component crashes",
        "detection": "\"Cannot access 'X' before initialization\" error in console",
        "typical_cause": "Circular imports; incorrect module initialization order",
        "recovery": "Fix import order; use lazy initialization",
        "prevention": "Lint rules for initialization; import graph analysis"
      },
      {
        "slug": "state_inconsistency",
        "description": "UI state diverges from database state; user sees stale or incorrect data",
        "detection": "Refresh changes displayed values; concurrent users see different data",
        "typical_cause": "Optimistic updates without reconciliation; missing subscription or refetch",
        "recovery": "Force refetch on error; reconcile state with DB on mount",
        "prevention": "Subscribe to DB changes; validate state after mutations"
      },
      {
        "slug": "lock_collision",
        "description": "Two processes attempt to acquire the same lock; one is stolen",
        "detection": "\"AbortError: Lock was stolen by another request\" in console",
        "typical_cause": "Multiple concurrent writes to same resource; missing lock management",
        "recovery": "Implement lock queuing; retry with backoff",
        "prevention": "Serialize critical writes; use transactions"
      },
      {
        "slug": "missing_permission_check",
        "description": "Action is allowed that should require a permission check; or blocked when it should not be",
        "detection": "User can take actions they shouldn't; or legitimate user is blocked",
        "typical_cause": "Permission check missing from action handler; wrong role check",
        "recovery": "Add check; audit all action handlers for permission enforcement",
        "prevention": "Require permission check declaration for all write actions"
      }
    ],
    "parent": null,
    "children": [],
    "tags": [
      "reliability",
      "error-handling",
      "anti-pattern",
      "debugging",
      "prevention"
    ],
    "synonyms": [
      "error type",
      "failure mode",
      "system anti-pattern"
    ]
  }
] as ConceptRegister[];

export const CONCEPT_REGISTERS_BY_SLUG: Record<string, ConceptRegister> = Object.fromEntries(
  CONCEPT_REGISTERS.map((register) => [register.slug, register])
);

export const ACTIVE_CONCEPT_REGISTER_SLUGS = CONCEPT_REGISTERS
  .filter((register) => register.status === 'active')
  .map((register) => register.slug);

// ─── Enumerated vocabularies ──────────────────────────────────────────────────
// One const + string-literal union per enumerated dimension (and per
// matrix-style register value set). Import these where code implements the
// corresponding register so vocabulary drift fails the typecheck.

/** decision-rights — register-level value rows (status: draft) */
export const DECISION_RIGHTS__VALUES = ["fixed","delegated","conditional"] as const;
export type DecisionRightsValue = (typeof DECISION_RIGHTS__VALUES)[number];

/** delegation — register-level value rows (status: draft) */
export const DELEGATION__VALUES = ["active","expired","revoked","pending_acceptance"] as const;
export type DelegationValue = (typeof DELEGATION__VALUES)[number];

/** permissions-authorization → layer (status: draft) */
export const PERMISSIONS_AUTHORIZATION__LAYER = ["capability","scope","policy"] as const;
export type PermissionsAuthorizationLayer = (typeof PERMISSIONS_AUTHORIZATION__LAYER)[number];

/** permissions-authorization → cascade_mode (status: draft) */
export const PERMISSIONS_AUTHORIZATION__CASCADE_MODE = ["all","children","none"] as const;
export type PermissionsAuthorizationCascadeMode = (typeof PERMISSIONS_AUTHORIZATION__CASCADE_MODE)[number];

/** organizational-hierarchy → unit_type (status: draft) */
export const ORGANIZATIONAL_HIERARCHY__UNIT_TYPE = ["division","department","team","squad","individual"] as const;
export type OrganizationalHierarchyUnitType = (typeof ORGANIZATIONAL_HIERARCHY__UNIT_TYPE)[number];

/** tier-derivation — register-level value rows (status: draft) */
export const TIER_DERIVATION__VALUES = ["executive","middle","bottom"] as const;
export type TierDerivationValue = (typeof TIER_DERIVATION__VALUES)[number];

/** catchball-governance → ball_stance (status: draft) */
export const CATCHBALL_GOVERNANCE__BALL_STANCE = ["request","proposal","transit-down","transit-up","complete"] as const;
export type CatchballGovernanceBallStance = (typeof CATCHBALL_GOVERNANCE__BALL_STANCE)[number];

/** catchball-governance → loop_state (status: draft) */
export const CATCHBALL_GOVERNANCE__LOOP_STATE = ["OPEN","PROPOSED","REVISION_REQUESTED","ACCEPTED","FINAL_REJECTED"] as const;
export type CatchballGovernanceLoopState = (typeof CATCHBALL_GOVERNANCE__LOOP_STATE)[number];

/** catchball-governance → loop_status (status: draft) */
export const CATCHBALL_GOVERNANCE__LOOP_STATUS = ["in_flight","blocked"] as const;
export type CatchballGovernanceLoopStatus = (typeof CATCHBALL_GOVERNANCE__LOOP_STATUS)[number];

/** ball-stance — register-level value rows (status: draft) */
export const BALL_STANCE__VALUES = ["request","proposal","transit-down","transit-up","complete"] as const;
export type BallStanceValue = (typeof BALL_STANCE__VALUES)[number];

/** legitimacy-marking — register-level value rows (status: draft) */
export const LEGITIMACY_MARKING__VALUES = ["draft","proposed","preliminary"] as const;
export type LegitimacyMarkingValue = (typeof LEGITIMACY_MARKING__VALUES)[number];

/** catchball-loop-state — register-level value rows (status: active) */
export const CATCHBALL_LOOP_STATE__VALUES = ["OPEN","PROPOSED","REVISION_REQUESTED","ACCEPTED","FINAL_REJECTED"] as const;
export type CatchballLoopStateValue = (typeof CATCHBALL_LOOP_STATE__VALUES)[number];

/** catchball-loop-status — register-level value rows (status: draft) */
export const CATCHBALL_LOOP_STATUS__VALUES = ["in_flight","blocked"] as const;
export type CatchballLoopStatusValue = (typeof CATCHBALL_LOOP_STATUS__VALUES)[number];

/** ball-waiting-state — register-level value rows (status: active) */
export const BALL_WAITING_STATE__VALUES = ["sent","with_them","overdue"] as const;
export type BallWaitingStateValue = (typeof BALL_WAITING_STATE__VALUES)[number];

/** loop-readiness-gates — register-level value rows (status: draft) */
export const LOOP_READINESS_GATES__VALUES = ["interpretation_written","all_tracks_ready","constraints_validated","dependencies_mapped","risks_identified","resource_committed","legal_review_complete","budget_alignment_confirmed"] as const;
export type LoopReadinessGatesValue = (typeof LOOP_READINESS_GATES__VALUES)[number];

/** catchball-blocker — register-level value rows (status: draft) */
export const CATCHBALL_BLOCKER__VALUES = ["criteria_not_locked","budget_misalignment","dependency_external","interpretation_unclear","team_capacity_insufficient","conflicting_guardrails","escalation_needed","missing_decision_rights","missing_stakeholder_input"] as const;
export type CatchballBlockerValue = (typeof CATCHBALL_BLOCKER__VALUES)[number];

/** item-readiness-status-contextual → readiness_state (status: draft) */
export const ITEM_READINESS_STATUS_CONTEXTUAL__READINESS_STATE = ["ready","not_ready","conditional"] as const;
export type ItemReadinessStatusContextualReadinessState = (typeof ITEM_READINESS_STATUS_CONTEXTUAL__READINESS_STATE)[number];

/** item-readiness-status-contextual → blockers (status: draft) */
export const ITEM_READINESS_STATUS_CONTEXTUAL__BLOCKERS = ["missing_value_proposal","missing_success_metric","unresolved_dependency","criteria_mismatch","scope_out_of_bounds","stakeholder_not_aligned","duplicate_pending"] as const;
export type ItemReadinessStatusContextualBlockers = (typeof ITEM_READINESS_STATUS_CONTEXTUAL__BLOCKERS)[number];

/** work-type-register — register-level value rows (status: draft) */
export const WORK_TYPE_REGISTER__VALUES = ["novel_build","bug_fix","tech_debt","safe_feature","operational","venture_bet","known_replication"] as const;
export type WorkTypeRegisterValue = (typeof WORK_TYPE_REGISTER__VALUES)[number];

/** source-type-register — register-level value rows (status: draft) */
export const SOURCE_TYPE_REGISTER__VALUES = ["sales_request","customer_council","strategic_initiative","internal_idea","regulatory_requirement","technical_debt","partner_request"] as const;
export type SourceTypeRegisterValue = (typeof SOURCE_TYPE_REGISTER__VALUES)[number];

/** session-type-decision-class-mapping → decision_class (status: draft) */
export const SESSION_TYPE_DECISION_CLASS_MAPPING__DECISION_CLASS = ["strategic_prioritization","quarterly_planning","vendor_or_partner_selection","feature_triage","risk_assessment","resource_allocation","idea_generation_ranking"] as const;
export type SessionTypeDecisionClassMappingDecisionClass = (typeof SESSION_TYPE_DECISION_CLASS_MAPPING__DECISION_CLASS)[number];

/** session-type-decision-class-mapping → output_shape (status: draft) */
export const SESSION_TYPE_DECISION_CLASS_MAPPING__OUTPUT_SHAPE = ["ranked_list","selected_set","scored_matrix","binary_decision","weighted_allocation"] as const;
export type SessionTypeDecisionClassMappingOutputShape = (typeof SESSION_TYPE_DECISION_CLASS_MAPPING__OUTPUT_SHAPE)[number];

/** session-state — register-level value rows (status: draft) */
export const SESSION_STATE__VALUES = ["setup","open_for_participants","live","paused","completed","published"] as const;
export type SessionStateValue = (typeof SESSION_STATE__VALUES)[number];

/** session-participant-roles — register-level value rows (status: draft) */
export const SESSION_PARTICIPANT_ROLES__VALUES = ["facilitator","evaluator","worker","observer"] as const;
export type SessionParticipantRolesValue = (typeof SESSION_PARTICIPANT_ROLES__VALUES)[number];

/** criteria-set-versioned → status (status: draft) */
export const CRITERIA_SET_VERSIONED__STATUS = ["draft","active","locked","archived"] as const;
export type CriteriaSetVersionedStatus = (typeof CRITERIA_SET_VERSIONED__STATUS)[number];

/** score-divergence → resolution (status: draft) */
export const SCORE_DIVERGENCE__RESOLUTION = ["discussion","re_score","accept_outlier","facilitator_override"] as const;
export type ScoreDivergenceResolution = (typeof SCORE_DIVERGENCE__RESOLUTION)[number];

/** published-priority-list → status (status: draft) */
export const PUBLISHED_PRIORITY_LIST__STATUS = ["draft","published","superseded"] as const;
export type PublishedPriorityListStatus = (typeof PUBLISHED_PRIORITY_LIST__STATUS)[number];

/** notification-event-type → event_type (status: active) */
export const NOTIFICATION_EVENT_TYPE__EVENT_TYPE = ["session_scheduled","session_updated","session_cancelled","session_starting_soon","session_started","session_completed","session_results_published","cycle_created","cycle_phase_changed","cycle_launched","cycle_completed","cycle_priorities_published","task_assigned","task_reminder","task_closing_soon","task_closed","task_results_ready","meeting_scheduled","meeting_updated","meeting_cancelled","meeting_reminder","steward_assigned","item_state_changed","item_ready_for_ranking","item_deprioritized","catchball_ball_tossed_to_middle","catchball_ball_tossed_to_bottom","catchball_ball_returned","catchball_level_involved","catchball_proposal_submitted","catchball_proposal_accepted","catchball_proposal_returned","catchball_proposal_rejected","reminder","mention","comment_reply","assignment","custom","catchball_ball_received","catchball_ball_claimed","catchball_ball_released","catchball_loop_closed","catchball_another_round","catchball_ball_overdue","catchball_ball_overdue_sender","v2mom_asked","v2mom_ask_withdrawn","v2mom_submitted","v2mom_submission_withdrawn","v2mom_approved","v2mom_returned","v2mom_published","v2mom_reopen_requested","v2mom_reopened","v2mom_reopen_declined","v2mom_ask_overdue","v2mom_ask_overdue_asker","participation_requested","participation_responded","participation_withdrawn","plan_of_record_authorized","structure_reorg_authorized","item_pool_published","idea_submitted","condition_broken","commitment_term_tripped","decision_right_delegated","track_lock_engaged","track_lock_released","track_created","track_bound_to_cycle","session_linked_to_track","invitation_sent","invitation_accepted","resource_claim_declared","resource_claim_withdrawn","plan_item_placed","plan_item_removed","decision_recorded","commitment_created","commitment_status_changed","signal_event_classified","signal_event_confirmed","signal_event_dismissed","signal_verdict_recorded","signal_event_reopened","agent_finding_accepted","twins_cluster_item_closed","twins_cluster_accepted","session_created","session_invited","session_rank_overridden","group_scores_withdrawn","plan_version_created","org_structure_edited","track_owner_assigned","watched_item_changed","followed_record_changed"] as const;
export type NotificationEventTypeEventType = (typeof NOTIFICATION_EVENT_TYPE__EVENT_TYPE)[number];

/** platform-module — register-level value rows (status: draft) */
export const PLATFORM_MODULE__VALUES = ["organizational_hierarchy","item_prioritization","session_management","evidence_capture","relationships_mapping","external_integrations","catchball_governance","decision_rights_model","advanced_analytics","ai_recommendations","multi_org_governance"] as const;
export type PlatformModuleValue = (typeof PLATFORM_MODULE__VALUES)[number];

/** module-readiness-state — register-level value rows (status: draft) */
export const MODULE_READINESS_STATE__VALUES = ["active","built_incomplete","coming_soon","planned"] as const;
export type ModuleReadinessStateValue = (typeof MODULE_READINESS_STATE__VALUES)[number];

/** feature-flag → wired (status: draft) */
export const FEATURE_FLAG__WIRED = ["wired_and_selectable","defined_not_wired"] as const;
export type FeatureFlagWired = (typeof FEATURE_FLAG__WIRED)[number];

/** configuration-metadata-enforcement → enforcement_type (status: draft) */
export const CONFIGURATION_METADATA_ENFORCEMENT__ENFORCEMENT_TYPE = ["restricts","recommends","routes","populates"] as const;
export type ConfigurationMetadataEnforcementEnforcementType = (typeof CONFIGURATION_METADATA_ENFORCEMENT__ENFORCEMENT_TYPE)[number];

/** configuration-next-step-routing — register-level value rows (status: draft) */
export const CONFIGURATION_NEXT_STEP_ROUTING__VALUES = ["drop_in_config","group_plus_session","catchball_config","planning_config"] as const;
export type ConfigurationNextStepRoutingValue = (typeof CONFIGURATION_NEXT_STEP_ROUTING__VALUES)[number];

/** configuration-workflow-footprint — register-level value rows (status: draft) */
export const CONFIGURATION_WORKFLOW_FOOTPRINT__VALUES = ["minimal","standard","full"] as const;
export type ConfigurationWorkflowFootprintValue = (typeof CONFIGURATION_WORKFLOW_FOOTPRINT__VALUES)[number];

/** landscape-metaphor — register-level value rows (status: draft) */
export const LANDSCAPE_METAPHOR__VALUES = ["situation","landmark","path","destination","hazard","instrument"] as const;
export type LandscapeMetaphorValue = (typeof LANDSCAPE_METAPHOR__VALUES)[number];

/** visual-primitive — register-level value rows (status: draft) */
export const VISUAL_PRIMITIVE__VALUES = ["situation","landmark","hazard","instrument","path","destination"] as const;
export type VisualPrimitiveValue = (typeof VISUAL_PRIMITIVE__VALUES)[number];

/** hazard-wayfinding — register-level value rows (status: draft) */
export const HAZARD_WAYFINDING__VALUES = ["outcome_ambiguity","reality_ambiguity","gap_ambiguity","scope_ambiguity","structural_ambiguity","value_ambiguity","decision_ambiguity","alignment_ambiguity","execution_ambiguity","adaptation_ambiguity"] as const;
export type HazardWayfindingValue = (typeof HAZARD_WAYFINDING__VALUES)[number];

/** instrument-wayfinding — register-level value rows (status: draft) */
export const INSTRUMENT_WAYFINDING__VALUES = ["outcome_lens","gap_finder","value_compass","structure_builder","relationship_mapper","criteria_builder"] as const;
export type InstrumentWayfindingValue = (typeof INSTRUMENT_WAYFINDING__VALUES)[number];

/** landmark-wayfinding — register-level value rows (status: draft) */
export const LANDMARK_WAYFINDING__VALUES = ["outcome_landmark","reality_landmark","gap_landmark","value_landmark","structure_landmark","alignment_landmark","execution_landmark"] as const;
export type LandmarkWayfindingValue = (typeof LANDMARK_WAYFINDING__VALUES)[number];

/** path-wayfinding — register-level value rows (status: draft) */
export const PATH_WAYFINDING__VALUES = ["yes_path","no_path","i_dont_know_path","loopback_path"] as const;
export type PathWayfindingValue = (typeof PATH_WAYFINDING__VALUES)[number];

/** destination-wayfinding → visibility_state (status: draft) */
export const DESTINATION_WAYFINDING__VISIBILITY_STATE = ["visible_unreached","reached","selected"] as const;
export type DestinationWayfindingVisibilityState = (typeof DESTINATION_WAYFINDING__VISIBILITY_STATE)[number];

/** state-clarity-wayfinding — register-level value rows (status: draft) */
export const STATE_CLARITY_WAYFINDING__VALUES = ["not_started","active","yes_resolved","no_declined","i_dont_know"] as const;
export type StateClarityWayfindingValue = (typeof STATE_CLARITY_WAYFINDING__VALUES)[number];

/** strategy-assumptions → assumption_type (status: draft) */
export const STRATEGY_ASSUMPTIONS__ASSUMPTION_TYPE = ["market","technical","organizational","competitive","regulatory"] as const;
export type StrategyAssumptionsAssumptionType = (typeof STRATEGY_ASSUMPTIONS__ASSUMPTION_TYPE)[number];

/** strategy-assumptions → confidence_level (status: draft) */
export const STRATEGY_ASSUMPTIONS__CONFIDENCE_LEVEL = ["speculative","informed","validated","proven"] as const;
export type StrategyAssumptionsConfidenceLevel = (typeof STRATEGY_ASSUMPTIONS__CONFIDENCE_LEVEL)[number];

/** plan-of-record → status (status: draft) */
export const PLAN_OF_RECORD__STATUS = ["draft","ratified","superseded"] as const;
export type PlanOfRecordStatus = (typeof PLAN_OF_RECORD__STATUS)[number];

/** state-transition-rule → transition_type (status: proposed) */
export const STATE_TRANSITION_RULE__TRANSITION_TYPE = ["manual","automatic","conditional"] as const;
export type StateTransitionRuleTransitionType = (typeof STATE_TRANSITION_RULE__TRANSITION_TYPE)[number];

/** temporal-constraint-type — register-level value rows (status: proposed) */
export const TEMPORAL_CONSTRAINT_TYPE__VALUES = ["hard_deadline","soft_target","availability_window","timeout","recurrence","cooling_off_period"] as const;
export type TemporalConstraintTypeValue = (typeof TEMPORAL_CONSTRAINT_TYPE__VALUES)[number];

/** system-failure-type — register-level value rows (status: proposed) */
export const SYSTEM_FAILURE_TYPE__VALUES = ["silent_db_failure","rls_violation","uninitialized_variable","state_inconsistency","lock_collision","missing_permission_check"] as const;
export type SystemFailureTypeValue = (typeof SYSTEM_FAILURE_TYPE__VALUES)[number];
